// Terrain Intelligence System - Advanced Route Planning and Analysis
// Provides comprehensive terrain analysis, route optimization, and predictive maintenance
// Features: Interactive mapping, weather integration, component wear prediction, mission planning
import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMapEvents, useMap } from 'react-leaflet';
import { Calendar, Clock, MapPin, Navigation, Truck, Search, Save, Package, RefreshCw, Crosshair, Plus, Sparkles } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useTheme } from '../contexts/ThemeContext';
import { geocodeAddress, reverseGeocode, parseCoordinates, searchAddresses } from '../utils/geocoding';
import { fetchRoute, calculateRouteStats, getRouteColor, ROUTE_PROFILES } from '../utils/routing';
import { fetchWeatherForecast, detectTerrainType } from '../utils/weather';
import { analyzeTerrain } from '../utils/fractalAPI';
import { analyzeRouteReal, transformRealDataForDisplay } from '../utils/realTerrainAnalysisAPI';
import { checkMLServiceHealth, enhanceAndAnalyze } from '../utils/mlEnhancementAPI';
import TerrainAnalysisCard from './TerrainAnalysisCard';
import RouteAnalysisCard from './RouteAnalysisCard';
import RealTerrainMetrics from './RealTerrainMetrics';
import MapDiagnostics from './MapDiagnostics';
import EnhancedComparisonView from './EnhancedComparisonView';
import { saveMission, createMissionData } from '../utils/missionStorageAPI';
import componentStressProfile from '../data/component_stress_profile.json';

// Fix for default marker icon in React-Leaflet
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Custom marker icons
const startIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const destinationIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Pale green marker icon for original start in reroute mode
const paleGreenMarkerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
  className: 'opacity-50'
});

// Pale red marker icon for original destination in reroute mode
const paleRedMarkerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
  className: 'opacity-50'
});

// Map click handler component
const MapClickHandler = ({ 
  inputMode, 
  startMarker, 
  setStartMarker, 
  destinationMarker, 
  setDestinationMarker,
  onClearRoutes 
}) => {
  useMapEvents({
    click(e) {
      if (inputMode === 'pin') {
        if (!startMarker) {
          // Set start marker
          setStartMarker(e.latlng);
          // Clear any existing routes
          if (onClearRoutes) onClearRoutes();
        } else if (!destinationMarker) {
          // Set destination marker
          setDestinationMarker(e.latlng);
          // Clear any existing routes
          if (onClearRoutes) onClearRoutes();
        }
      }
    },
  });
  return null;
};

// Map resizer component - fixes tile loading issue when tab becomes visible
const MapResizer = () => {
  const map = useMap();
  
  useEffect(() => {
    // Function to invalidate map size
    const invalidateMapSize = () => {
      map.invalidateSize();
      console.log('🗺️ Map size recalculated');
    };
    
    // Invalidate immediately
    invalidateMapSize();
    
    // Invalidate after delays to catch tab visibility changes
    const timer1 = setTimeout(invalidateMapSize, 100);
    const timer2 = setTimeout(invalidateMapSize, 300);
    const timer3 = setTimeout(invalidateMapSize, 500);
    
    // Listen for window resize events (catches tab becoming visible)
    window.addEventListener('resize', invalidateMapSize);
    
    // Use Intersection Observer to detect when map becomes visible
    const mapContainer = map.getContainer();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          console.log('🗺️ Map became visible, recalculating size...');
          invalidateMapSize();
          // Invalidate again after a short delay
          setTimeout(invalidateMapSize, 100);
        }
      });
    }, { threshold: 0.1 });
    
    observer.observe(mapContainer);
    
    // Cleanup
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      window.removeEventListener('resize', invalidateMapSize);
      observer.disconnect();
    };
  }, [map]);
  
  return null;
};

const TerrainIntelligence = ({ vehicles, missionToLoad, onMissionLoaded }) => {
  const { getThemeClasses, theme } = useTheme();
  const themeClasses = getThemeClasses();
  
  // Load unit system from settings
  const savedSettings = localStorage.getItem('fracadapt-settings');
  let isMetric = true; // Default to Metric
  if (savedSettings) {
    try {
      const settings = JSON.parse(savedSettings);
      isMetric = settings.general?.unitSystem === 'Metric';
    } catch (error) {
      console.warn('Failed to load settings');
    }
  }
  
  const weightUnit = isMetric ? 'kg' : 'lbs';
  
  // State for mission planning
  const [missionName, setMissionName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [duration, setDuration] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [additionalCargoWeight, setAdditionalCargoWeight] = useState(0); // Additional cargo in lbs
  const [startLocation, setStartLocation] = useState('');
  const [destinationLocation, setDestinationLocation] = useState('');
  const [inputMode, setInputMode] = useState('address'); // 'address', 'coordinates', 'pin'
  const [mapType, setMapType] = useState('terrain'); // 'satellite', 'terrain', 'topography'

  // State for map pins
  const [startMarker, setStartMarker] = useState(null);
  const [destinationMarker, setDestinationMarker] = useState(null);

  // State for address suggestions
  const [startSuggestions, setStartSuggestions] = useState([]);
  const [destinationSuggestions, setDestinationSuggestions] = useState([]);
  const [showStartSuggestions, setShowStartSuggestions] = useState(false);
  const [showDestinationSuggestions, setShowDestinationSuggestions] = useState(false);

  // State for location names
  const [startLocationName, setStartLocationName] = useState('');
  const [destinationLocationName, setDestinationLocationName] = useState('');

  // State for routes
  const [routes, setRoutes] = useState([]);
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);
  const [routeProfile, setRouteProfile] = useState(ROUTE_PROFILES.DRIVING_CAR);
  const [isLoadingRoute, setIsLoadingRoute] = useState(false);
  const [showRouteStats, setShowRouteStats] = useState(false);
  const [candidateRoutes, setCandidateRoutes] = useState([]);
  const [routeAnalysisData, setRouteAnalysisData] = useState([]);

  // State for weather
  const [weatherData, setWeatherData] = useState(null);
  const [terrainType, setTerrainType] = useState('mixed');
  const [isLoadingWeather, setIsLoadingWeather] = useState(false);

  // State for terrain analysis
  const [terrainAnalysisData, setTerrainAnalysisData] = useState(null);
  const [showTerrainAnalysis, setShowTerrainAnalysis] = useState(false);
  
  // State for REAL fractal analysis
  const [fractalAnalysisData, setFractalAnalysisData] = useState(null);
  const [isLoadingFractal, setIsLoadingFractal] = useState(false);

  // State for ML enhancement
  const [mlServiceHealthy, setMlServiceHealthy] = useState(null);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhancedAnalysis, setEnhancedAnalysis] = useState(null);
  const [enhancementError, setEnhancementError] = useState(null);
  const [showEnhancedComparison, setShowEnhancedComparison] = useState(false);
  const [showEnhancementModal, setShowEnhancementModal] = useState(false);
  const [enhancementElapsed, setEnhancementElapsed] = useState(0);
  const [enhancementGridInfo, setEnhancementGridInfo] = useState(null);
  const [enhancementSuccess, setEnhancementSuccess] = useState(false);

  // State for Analyze Mission modal
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [analysisStage, setAnalysisStage] = useState(0);
  const [analysisElapsed, setAnalysisElapsed] = useState(0);
  const [analysisSuccess, setAnalysisSuccess] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const [analysisRouteCount, setAnalysisRouteCount] = useState(0);

  // State for GPS location
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);
  
  // State for map instance
  const [mapInstance, setMapInstance] = useState(null);
  
  // State for tracking loaded mission (for change detection)
  const [loadedMissionId, setLoadedMissionId] = useState(null);
  const [loadedMissionFingerprint, setLoadedMissionFingerprint] = useState(null);
  const [showSaveOptionsModal, setShowSaveOptionsModal] = useState(false);
  const [missionHasChanges, setMissionHasChanges] = useState(false);
  
  // State for reroute mode
  const [rerouteMode, setRerouteMode] = useState(false);
  const [originalRouteData, setOriginalRouteData] = useState(null);


  // Afghanistan center coordinates
  const defaultCenter = [34.5553, 69.2075]; // Kabul, Afghanistan
  const defaultZoom = 6;

  // Load mission data when missionToLoad prop changes (from SavedMissions)
  useEffect(() => {
    if (missionToLoad) {
      const mission = missionToLoad;
      console.log('📥 Loading mission data:', mission);
      console.log('Mission structure:', {
        missionInfo: mission.missionInfo,
        locationData: mission.locationData,
        analysisData: mission.analysisData,
        isReroute: mission.isReroute
      });
      
      console.log('🔍 Additional Cargo Weight Debug:', {
        fromMissionInfo: mission.missionInfo?.additionalCargoWeight,
        fromTopLevel: mission.additionalCargoWeight,
        fullMissionInfo: mission.missionInfo
      });
      
      // Check if this is a reroute operation
      if (mission.isReroute) {
        console.log('🔄 Reroute mode activated');
        setRerouteMode(true);
        
        // Store original route data
        setOriginalRouteData({
          startMarker: mission.locationData.startMarker,
          destinationMarker: mission.locationData.destinationMarker,
          routes: mission.locationData.routes,
          startLocationName: mission.locationData.startLocationName,
          destinationLocationName: mission.locationData.destinationLocationName
        });
        
        // Clear current markers and routes to allow new route selection
        setStartMarker(null);
        setDestinationMarker(null);
        setRoutes([]);
        setCandidateRoutes([]);
        setRouteAnalysisData([]);
        setShowRouteStats(false);
        setStartLocation('');
        setDestinationLocation('');
        setStartLocationName('');
        setDestinationLocationName('');
      } else {
        // Normal mission load
        setRerouteMode(false);
        setOriginalRouteData(null);
        
        // Load location data
        setStartLocation(mission.locationData.startLocation || '');
        setDestinationLocation(mission.locationData.destinationLocation || '');
        setStartLocationName(mission.locationData.startLocationName || '');
        setDestinationLocationName(mission.locationData.destinationLocationName || '');
        
        // Load markers
        if (mission.locationData.startMarker) {
          setStartMarker(mission.locationData.startMarker);
          console.log('✅ Start marker loaded:', mission.locationData.startMarker);
        }
        if (mission.locationData.destinationMarker) {
          setDestinationMarker(mission.locationData.destinationMarker);
          console.log('✅ Destination marker loaded:', mission.locationData.destinationMarker);
        }
        
        // Load routes
        if (mission.locationData.routes && mission.locationData.routes.length > 0) {
          setRoutes(mission.locationData.routes);
          setCandidateRoutes(mission.locationData.routes);
          setSelectedRouteIndex(mission.locationData.selectedRouteIndex || 0);
          setShowRouteStats(true);
          console.log(`✅ ${mission.locationData.routes.length} route(s) loaded`);
        }
        
        // Load analysis data
        if (mission.analysisData) {
          setRouteAnalysisData(mission.analysisData.routeAnalysisData || []);
          setWeatherData(mission.analysisData.weatherData || null);
          setTerrainType(mission.analysisData.terrainType || 'mixed');
          setShowTerrainAnalysis(true);
          console.log('✅ Analysis data loaded:', {
            routeAnalysisCount: mission.analysisData.routeAnalysisData?.length || 0,
            hasWeather: !!mission.analysisData.weatherData,
            terrainType: mission.analysisData.terrainType
          });
        }
      }
      
      // Load mission info (common for both modes)
      setMissionName(mission.missionInfo.name);
      setStartDate(mission.missionInfo.startDate || '');
      setDuration(mission.missionInfo.duration || '');
      setSelectedVehicle(mission.missionInfo.vehicleInfo?.uniqueId || '');
      
      // Try loading from both possible locations
      const cargoWeight = mission.missionInfo?.additionalCargoWeight ?? mission.additionalCargoWeight ?? 0;
      console.log('📦 Setting Additional Cargo Weight to:', cargoWeight);
      setAdditionalCargoWeight(cargoWeight);
      
      // Set loaded mission tracking
      setLoadedMissionId(mission.id);
      setLoadedMissionFingerprint(mission.fingerprint);
      setMissionHasChanges(false); // No changes yet since we just loaded
      
      // Center map on route if available
      if (mission.locationData.startMarker && mapInstance) {
        const start = mission.locationData.startMarker;
        mapInstance.setView([start.lat, start.lng], 10, {
          animate: true,
          duration: 1.0
        });
        console.log('🗺️ Map centered on mission start location');
      }
      
      // Notify parent that mission has been loaded
      if (onMissionLoaded) {
        onMissionLoaded();
      }
      
      console.log('✅ Mission loaded successfully into Terrain Intelligence');
    }
  }, [missionToLoad, mapInstance]); // React to missionToLoad changes

  // Map tile URLs with proper configurations
  const tileConfigs = {
    terrain: {
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    },
    satellite: {
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
      maxZoom: 18,
    },
    topography: {
      url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
      attribution: 'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)',
      maxZoom: 17,
    },
  };

  // Get current GPS location
  const handleGetCurrentLocation = async () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setIsGettingLocation(true);
    setLocationError(null);

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          }
        );
      });

      const { latitude, longitude, accuracy } = position.coords;
      
      console.log('📍 GPS Location obtained:', {
        lat: latitude,
        lng: longitude,
        accuracy: `${accuracy}m`
      });

      // Set as start marker
      const location = { lat: latitude, lng: longitude };
      setStartMarker(location);

      // Center map on the GPS location with appropriate zoom
      if (mapInstance) {
        mapInstance.setView([latitude, longitude], 13, {
          animate: true,
          duration: 1.0
        });
        console.log('🗺️ Map centered on GPS location');
      }

      // Reverse geocode to get address name
      try {
        const address = await reverseGeocode(latitude, longitude);
        setStartLocationName(address.display_name);
        setStartLocation(address.display_name);
        console.log('📍 Location name:', address.display_name);
      } catch (error) {
        console.warn('Failed to reverse geocode location:', error);
        setStartLocationName(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
        setStartLocation(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
      }

      // Show success message with accuracy
      const accuracyText = accuracy < 50 ? 'High accuracy' : accuracy < 100 ? 'Medium accuracy' : 'Low accuracy';
      alert(`✅ Current Location Set\n\n` +
            `Latitude: ${latitude.toFixed(6)}\n` +
            `Longitude: ${longitude.toFixed(6)}\n` +
            `Accuracy: ${accuracy.toFixed(0)}m (${accuracyText})\n\n` +
            `Location has been set as your start point.`);

    } catch (error) {
      console.error('❌ GPS Error:', error);
      
      let errorMessage = 'Failed to get your location.\n\n';
      
      switch (error.code) {
        case error.PERMISSION_DENIED:
          errorMessage += 'Location permission denied.\n\nPlease enable location access in your browser settings.';
          break;
        case error.POSITION_UNAVAILABLE:
          errorMessage += 'Location information unavailable.\n\nPlease check your device settings.';
          break;
        case error.TIMEOUT:
          errorMessage += 'Location request timed out.\n\nPlease try again.';
          break;
        default:
          errorMessage += error.message || 'Unknown error occurred.';
      }
      
      setLocationError(errorMessage);
      alert(errorMessage);
    } finally {
      setIsGettingLocation(false);
    }
  };

  // Fetch route when both markers are set
  const handleFetchRoute = async () => {
    if (!startMarker || !destinationMarker) {
      alert('Please set both start and destination points');
      return;
    }

    setIsLoadingRoute(true);
    
    // Clear old analysis data when fetching new route
    setRouteAnalysisData([]);
    setTerrainAnalysisData(null);
    setShowTerrainAnalysis(false);
    setFractalAnalysisData(null);
    
    try {
      // Request single route (no alternatives)
      const routeData = await fetchRoute(startMarker, destinationMarker, routeProfile, 0);
      setRoutes(routeData);
      setCandidateRoutes(routeData);
      setSelectedRouteIndex(0);
      setShowRouteStats(true);

      // Check if routes have elevation data
      const hasElevationData = routeData.some(route => route.hasRealElevation);
      
      if (!hasElevationData) {
        console.error('❌ No elevation data available for routes');
        alert('Elevation data unavailable!\n\nCannot perform terrain analysis without SRTM elevation data.\n\nPlease check:\n- OpenTopography API key is configured in .env\n- Route area is not too large (max 25 deg²)\n- API is accessible');
        // Clear routes since we can't analyze them
        setRoutes([]);
        setCandidateRoutes([]);
        setRouteAnalysisData([]);
        setShowRouteStats(false);
        return;
      }
      
      console.log(`✅ Got ${routeData.length} route(s) with elevation data - ready for analysis`);
      // Note: Terrain analysis will be triggered when user clicks "Analyze Mission"

      // Detect terrain type from main route
      if (routeData && routeData.length > 0) {
        const detectedTerrain = detectTerrainType(routeData[0].coordinates);
        setTerrainType(detectedTerrain);
      }
      
    } catch (error) {
      console.error('❌ Route fetch failed:', error);
      alert(`Failed to fetch route: ${error.message}`);
    } finally {
      setIsLoadingRoute(false);
    }
  };

  // Fetch weather when route and duration are available
  const handleFetchWeather = async () => {
    if (!startMarker || !duration || duration < 1) {
      return;
    }

    setIsLoadingWeather(true);
    try {
      const weather = await fetchWeatherForecast(startMarker, parseInt(duration));
      setWeatherData(weather);
    } catch (error) {
      console.error('Failed to fetch weather:', error);
      alert(`Failed to fetch weather: ${error.message}`);
    } finally {
      setIsLoadingWeather(false);
    }
  };

  // Detect when component becomes visible (tab switch)
  useEffect(() => {
    // Create a custom event that will trigger map resize
    const handleVisibilityChange = () => {
      console.log('🗺️ Terrain Intelligence tab became visible');
      // Trigger a resize event to force map recalculation
      window.dispatchEvent(new Event('resize'));
    };
    
    // Check if component is visible on mount
    const checkVisibility = () => {
      const isVisible = document.querySelector('[style*="display: block"]')?.contains(document.querySelector('.leaflet-container'));
      if (isVisible) {
        handleVisibilityChange();
      }
    };
    
    // Check immediately
    checkVisibility();
    
    // Check after delays
    setTimeout(checkVisibility, 100);
    setTimeout(checkVisibility, 300);
    setTimeout(checkVisibility, 500);
    
    return () => {
      // Cleanup if needed
    };
  }, []); // Run once on mount

  // Auto-fetch weather when mission details are complete
  useEffect(() => {
    if (startMarker && duration && duration > 0 && !isLoadingWeather) {
      handleFetchWeather();
    }
  }, [startMarker, duration]);

  // Generate comprehensive route analysis using REAL DATA (NO MOCK)
  const generateRouteAnalysis = async (route, routeIndex) => {
    const stats = calculateRouteStats(route);

    // Calculate bbox from route coordinates for real analysis
    const lats = route.coordinates.map(c => c.lat);
    const lngs = route.coordinates.map(c => c.lng);
    
    const bbox = {
      south: Math.min(...lats) - 0.02,
      north: Math.max(...lats) + 0.02,
      west: Math.min(...lngs) - 0.02,
      east: Math.max(...lngs) + 0.02
    };

    try {
      // Fetch REAL terrain analysis from backend
      console.log(`🔬 Fetching real terrain analysis for route ${routeIndex + 1}...`);
      
      // Don't use pre-fetched elevation data - let backend fetch full grid for accurate slope/roughness calculations
      // Using rawElevationData triggers lightweight mode with approximations
      const elevationData = null; // Force backend to fetch full SRTM grid
      
      // Get selected vehicle data
      const selectedVehicleData = vehicles?.find(v => v.uniqueId === selectedVehicle);
      const vehicleData = selectedVehicleData ? {
        cargoWeight: (selectedVehicleData.cargoWeight || 0) + additionalCargoWeight,
        componentHealth: selectedVehicleData.components || {
          engine: 92,
          suspension: 90,
          brakes: 85,
          transmission: 90
        }
      } : null;
      
      // Add duration and start date to route object for weather forecast calculation
      // Duration input is in days, convert to minutes for backend
      const durationInMinutes = duration ? parseInt(duration) * 24 * 60 : 0;
      const routeWithDuration = {
        ...route,
        duration: durationInMinutes,
        startDate: startDate || null,  // Pass start date for weather forecast
        routeName: missionName || 'Unnamed Route',
        startLocation: startLocationName || startLocation || 'Unknown',
        destinationLocation: destinationLocationName || destinationLocation || 'Unknown'
      };
      
      const realAnalysis = await analyzeRouteReal(routeWithDuration, elevationData, bbox, vehicleData);
      
      // Prepare mission inputs for display
      const missionInputs = {
        startDate: startDate,
        duration: duration,
        vehicle: selectedVehicleData ? `${selectedVehicleData.brand} ${selectedVehicleData.model}` : null,
        vehicleHealth: selectedVehicleData?.health || null,
        cargoCapacity: selectedVehicleData?.cargoWeight || null,
        additionalCargo: additionalCargoWeight,
        vehicleMileage: selectedVehicleData?.mileage || null,
        vehicleYear: selectedVehicleData?.year || null
      };
      
      // Transform real data to display format
      const analysisData = transformRealDataForDisplay(realAnalysis, route, missionName, vehicleData, missionInputs);
      analysisData.routeIndex = routeIndex;
      analysisData.routeType = getRouteTypeName(route.routeType || 'direct', routeIndex);
      analysisData.routeColor = getRouteColor(routeIndex);
      
      console.log(`✅ Real analysis complete for route ${routeIndex + 1}`);
      console.log('🔬 Component stress data:', analysisData.componentStress);
      console.log('🗺️  Elevation data available:', !!analysisData.elevationData);
      if (analysisData.elevationData) {
        console.log('   Grid size:', analysisData.elevationData.grid?.length, 'x', analysisData.elevationData.grid?.[0]?.length);
        console.log('   Source:', analysisData.elevationData.source);
      }
      return analysisData;
      
    } catch (error) {
      console.error(`❌ Real analysis failed for route ${routeIndex + 1}:`, error);
      // Re-throw error - no fallback to mock data
      throw new Error(`Terrain analysis failed: ${error.message}`);
    }
  };



  // Get route type information for analysis with consistent multipliers
  const getRouteTypeInfo = (routeType, routeIndex) => {
    const routeTypes = {
      direct: {
        elevationMultiplier: 1.0,
        slopeMultiplier: 1.0,
        complexityMultiplier: 1.0,
        ttiMultiplier: 1.0,
        curvatureMultiplier: 1.0,
        roughnessMultiplier: 1.0
      },
      'maintenance-optimized': {
        elevationMultiplier: 0.6,
        slopeMultiplier: 0.5,
        complexityMultiplier: 0.6,
        ttiMultiplier: 1.3,
        curvatureMultiplier: 0.7,
        roughnessMultiplier: 0.5
      },
      scenic: {
        elevationMultiplier: 1.4,
        slopeMultiplier: 1.3,
        complexityMultiplier: 1.5,
        ttiMultiplier: 0.8,
        curvatureMultiplier: 1.6,
        roughnessMultiplier: 1.4
      },
      highway: {
        elevationMultiplier: 0.7,
        slopeMultiplier: 0.6,
        complexityMultiplier: 0.7,
        ttiMultiplier: 1.2,
        curvatureMultiplier: 0.8,
        roughnessMultiplier: 0.6
      },
      alternative: {
        elevationMultiplier: 1.2,
        slopeMultiplier: 1.1,
        complexityMultiplier: 1.2,
        ttiMultiplier: 0.9,
        curvatureMultiplier: 1.3,
        roughnessMultiplier: 1.2
      }
    };

    return routeTypes[routeType] || routeTypes.direct;
  };

  // Get route type display name
  const getRouteTypeName = (routeType, routeIndex) => {
    if (routeIndex === 0) return 'Main Route';

    const typeNames = {
      'maintenance-optimized': `Maintenance-Optimized Route`,
      scenic: `Scenic Route`,
      highway: `Highway Route`,
      alternative: `Alternative Route`,
      direct: `Direct Route`
    };

    return typeNames[routeType] || `Alternative ${routeIndex}`;
  };

  // Calculate route stress level for color coding
  const calculateRouteStress = (route, complexityScore) => {
    if (complexityScore >= 70) return 'high';
    if (complexityScore >= 40) return 'medium';
    return 'low';
  };

  // Get route-specific surface type
  const getRouteSpecificSurfaceType = (routeType, routeIndex) => {
    const surfaceTypes = {
      direct: ['Paved Road', 'Gravel Road', 'Mixed Terrain'],
      scenic: ['Gravel Road', 'Dirt Track', 'Rocky Path', 'Mountain Trail'],
      highway: ['Paved Road', 'Asphalt Road'],
      alternative: ['Gravel Road', 'Dirt Track', 'Mixed Terrain']
    };

    const types = surfaceTypes[routeType] || surfaceTypes.direct;
    return types[Math.floor(Math.random() * types.length)];
  };

  // Get route-specific soil type
  const getRouteSpecificSoilType = (routeType, routeIndex) => {
    const soilTypes = {
      direct: ['Sandy Loam', 'Silty Loam', 'Compacted Earth'],
      scenic: ['Rocky', 'Mixed Alluvial', 'Gravelly', 'Clay'],
      highway: ['Compacted Earth', 'Sandy Loam'],
      alternative: ['Clay', 'Silty Loam', 'Mixed Alluvial']
    };

    const types = soilTypes[routeType] || soilTypes.direct;
    return types[Math.floor(Math.random() * types.length)];
  };

  // Get route-specific moisture conditions
  const getRouteSpecificMoisture = (routeType, routeIndex) => {
    const moistureTypes = {
      direct: ['Dry', 'Moderate'],
      scenic: ['Arid', 'Dry', 'Moist'],
      highway: ['Dry', 'Moderate'],
      alternative: ['Moderate', 'Moist', 'Wet']
    };

    const types = moistureTypes[routeType] || moistureTypes.direct;
    return types[Math.floor(Math.random() * types.length)];
  };

  // Get terrain classification
  const getTerrainClassification = (elevationGain, roughnessIndex, maxSlope) => {
    if (elevationGain > 800) return 'High Mountain';
    if (elevationGain > 500) return 'Mountainous';
    if (elevationGain > 200) return 'Hilly Terrain';
    if (roughnessIndex > 70) return 'Rough Terrain';
    if (maxSlope > 20) return 'Steep Terrain';
    return 'Moderate Terrain';
  };

  // Get recommended speed based on complexity (unit-aware)
  const getRecommendedSpeed = (complexityScore) => {
    const speedUnit = isMetric ? 'km/h' : 'mph';
    const convert = (kmh) => isMetric ? kmh : Math.round(kmh * 0.621371);
    
    if (complexityScore >= 80) return `${convert(10)}-${convert(20)} ${speedUnit}`;
    if (complexityScore >= 70) return `${convert(15)}-${convert(25)} ${speedUnit}`;
    if (complexityScore >= 50) return `${convert(25)}-${convert(35)} ${speedUnit}`;
    if (complexityScore >= 30) return `${convert(35)}-${convert(45)} ${speedUnit}`;
    return `${convert(40)}-${convert(60)} ${speedUnit}`;
  };

  // Generate route comparison data
  const generateRouteComparison = (route, routeIndex, complexityScore, tti, routeType) => {
    const advantages = [];
    const disadvantages = [];

    if (routeIndex === 0) {
      advantages.push('Shortest distance', 'Primary route recommendation');
      if (complexityScore < 40) advantages.push('Low terrain complexity');
      if (tti > 70) advantages.push('High traversability');
    } else {
      // Route-type specific advantages/disadvantages
      switch (routeType) {
        case 'maintenance-optimized':
          advantages.push('Minimizes vehicle wear', 'Lower maintenance costs', 'Gentler terrain', 'Extended component life');
          disadvantages.push('Slightly longer distance', 'May take more time');
          break;
        case 'scenic':
          advantages.push('Scenic mountain views', 'Less traffic congestion');
          disadvantages.push('Longer travel time', 'Higher fuel consumption', 'More challenging terrain');
          break;
        case 'highway':
          advantages.push('Faster average speed', 'Better road conditions', 'Lower terrain complexity');
          disadvantages.push('Slightly longer distance', 'More traffic');
          break;
        case 'alternative':
          advantages.push('Avoids main traffic routes', 'Moderate terrain difficulty');
          disadvantages.push('Longer distance', 'Less familiar route');
          break;
        default:
          advantages.push('Alternative path option');
          disadvantages.push('Longer distance');
      }

      if (complexityScore > 60) disadvantages.push('Higher terrain complexity');
      if (tti < 50) disadvantages.push('Lower traversability score');
    }

    return { advantages, disadvantages };
  };

  // Match route to terrain type based on characteristics
  const matchRouteToTerrain = (route, routeType) => {
    const elevationGain = route.ascent || 0;
    const routeDistance = route.distance / 1000; // Convert to km

    // Determine terrain type based on route characteristics and type
    let terrainMatch = null;

    if (routeType === 'maintenance-optimized') {
      // Maintenance-optimized routes prefer easier terrain
      if (elevationGain < 200) {
        terrainMatch = componentStressProfile.terrains.find(t =>
          t.terrain_type.name === 'Semi-Arid Steppe' ||
          t.terrain_type.name === 'Urban / Semi-Urban Terrain'
        );
      } else {
        terrainMatch = componentStressProfile.terrains.find(t =>
          t.terrain_type.name === 'Foothills & Valleys'
        );
      }
    } else if (routeType === 'scenic') {
      // Scenic routes go through more challenging terrain
      if (elevationGain > 600) {
        terrainMatch = componentStressProfile.terrains.find(t =>
          t.terrain_type.name === 'Mountainous Terrain' ||
          t.terrain_type.name === 'Mountain Pass Corridors'
        );
      } else if (elevationGain > 300) {
        terrainMatch = componentStressProfile.terrains.find(t =>
          t.terrain_type.name === 'Rocky Plateau' ||
          t.terrain_type.name === 'Foothills & Valleys'
        );
      } else {
        terrainMatch = componentStressProfile.terrains.find(t =>
          t.terrain_type.name === 'Hilly Desert'
        );
      }
    } else if (routeType === 'highway') {
      // Highway routes prefer flatter, more developed terrain
      terrainMatch = componentStressProfile.terrains.find(t =>
        t.terrain_type.name === 'Gravel Plains' ||
        t.terrain_type.name === 'Alluvial Plains'
      );
    } else {
      // Direct routes - moderate terrain
      terrainMatch = componentStressProfile.terrains.find(t =>
        t.terrain_type.name === 'Semi-Arid Steppe'
      );
    }

    // Fallback to a default terrain if no match found
    return terrainMatch || componentStressProfile.terrains.find(t =>
      t.terrain_type.name === 'Semi-Arid Steppe'
    );
  };

  // Calculate component degradation based on stress profile
  const calculateComponentDegradation = (stressProfile, routeDistance) => {
    const missionFactor = routeDistance / 100; // Normalize by 100km

    return {
      engine: {
        currentHealth: 92,
        stressLoad: stressProfile.engine_heat_load,
        degradationRate: Math.round(stressProfile.engine_heat_load * 0.15 * missionFactor),
        postMissionHealth: Math.max(0, 92 - Math.round(stressProfile.engine_heat_load * 0.15 * missionFactor))
      },
      brakes: {
        currentHealth: 85,
        stressLoad: stressProfile.brake_load_factor,
        degradationRate: Math.round(stressProfile.brake_load_factor * 0.2 * missionFactor),
        postMissionHealth: Math.max(0, 85 - Math.round(stressProfile.brake_load_factor * 0.2 * missionFactor))
      },
      suspension: {
        currentHealth: 88,
        stressLoad: stressProfile.suspension_stress_index,
        degradationRate: Math.round(stressProfile.suspension_stress_index * 0.18 * missionFactor),
        postMissionHealth: Math.max(0, 88 - Math.round(stressProfile.suspension_stress_index * 0.18 * missionFactor))
      },
      transmission: {
        currentHealth: 90,
        stressLoad: stressProfile.transmission_strain,
        degradationRate: Math.round(stressProfile.transmission_strain * 0.16 * missionFactor),
        postMissionHealth: Math.max(0, 90 - Math.round(stressProfile.transmission_strain * 0.16 * missionFactor))
      }
    };
  };

  // Generate maintenance alerts based on priority score
  const generateMaintenanceAlerts = (priorityScore, componentDegradation) => {
    const alerts = [];

    if (priorityScore > 80) {
      alerts.push({
        level: 'critical',
        message: 'Pre-mission inspection required - High stress terrain detected',
        icon: '🔴'
      });
    }

    // Check individual components
    Object.entries(componentDegradation).forEach(([component, data]) => {
      if (data.postMissionHealth < 50) {
        alerts.push({
          level: 'critical',
          message: `${component.charAt(0).toUpperCase() + component.slice(1)} replacement recommended before mission`,
          icon: '⚠️'
        });
      } else if (data.postMissionHealth < 70) {
        alerts.push({
          level: 'warning',
          message: `${component.charAt(0).toUpperCase() + component.slice(1)} service recommended after mission`,
          icon: '🟡'
        });
      }
    });

    if (alerts.length === 0) {
      alerts.push({
        level: 'good',
        message: 'Normal operation expected - No immediate maintenance required',
        icon: '🟢'
      });
    }

    return alerts;
  };

  // Track changes to mission planning fields
  useEffect(() => {
    if (loadedMissionId && loadedMissionFingerprint) {
      const currentFingerprint = createMissionFingerprint();
      const hasChanged = currentFingerprint !== loadedMissionFingerprint;
      setMissionHasChanges(hasChanged);
      
      if (hasChanged) {
        console.log('✏️ Mission modified - Save button enabled');
      }
    } else {
      // New mission - always allow saving
      setMissionHasChanges(true);
    }
  }, [missionName, startDate, duration, selectedVehicle, additionalCargoWeight, startMarker, destinationMarker, routeProfile, loadedMissionId, loadedMissionFingerprint]);

  // Auto-fetch route when both markers are set (but not if routes already exist from loaded mission)
  const fetchingRouteRef = useRef(false);
  useEffect(() => {
    if (startMarker && destinationMarker && !isLoadingRoute && !fetchingRouteRef.current && routes.length === 0) {
      // Debounce to avoid rapid re-triggers from marker drag events
      const timer = setTimeout(() => {
        if (!fetchingRouteRef.current && routes.length === 0) {
          fetchingRouteRef.current = true;
          handleFetchRoute().finally(() => {
            fetchingRouteRef.current = false;
          });
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [startMarker, destinationMarker, routeProfile]);
  
  // Auto-update fractal analysis when route selection changes
  useEffect(() => {
    if (routes.length > 0 && selectedRouteIndex >= 0 && fractalAnalysisData) {
      // If analysis exists but for different route, re-analyze
      if (fractalAnalysisData.routeIndex !== selectedRouteIndex) {
        console.log(`🔄 Route changed to ${selectedRouteIndex + 1}, updating analysis...`);
        handleTerrainAnalysis();
      }
    }
  }, [selectedRouteIndex]);

  // Check ML service health on component mount
  useEffect(() => {
    const checkHealth = async () => {
      const health = await checkMLServiceHealth();
      setMlServiceHealthy(health.healthy);
      if (health.healthy) {
        console.log('✅ ML service available:', health.device);
      } else {
        console.log('⚠️  ML service unavailable');
      }
    };
    checkHealth();
  }, []);

  // Load mission data when component mounts (from Saved Missions)
  useEffect(() => {
    if (window.loadMissionData) {
      const mission = window.loadMissionData;
      console.log('📥 Loading saved mission:', mission);
      console.log('Mission structure check:', {
        hasMissionInfo: !!mission.missionInfo,
        hasLocationData: !!mission.locationData,
        hasAnalysisData: !!mission.analysisData,
        missionName: mission.missionInfo?.name || mission.name
      });
      
      try {
        // Load mission info (handle both nested and flat structures)
        const missionInfo = mission.missionInfo || mission;
        setMissionName(missionInfo.name || '');
        setStartDate(missionInfo.startDate || '');
        setDuration(missionInfo.duration || '');
        setSelectedVehicle(missionInfo.selectedVehicle || '');
        setRouteProfile(missionInfo.routeProfile || ROUTE_PROFILES.DRIVING_CAR);

        // Load location data
        if (mission.locationData.startMarker) {
          setStartMarker(mission.locationData.startMarker);
          setStartLocationName(mission.locationData.startLocationName || '');
          setStartLocation(mission.locationData.startLocationName || '');
        }
        
        if (mission.locationData.destinationMarker) {
          setDestinationMarker(mission.locationData.destinationMarker);
          setDestinationLocationName(mission.locationData.destinationLocationName || '');
          setDestinationLocation(mission.locationData.destinationLocationName || '');
        }

        // Load route data
        if (mission.locationData.routes && mission.locationData.routes.length > 0) {
          setRoutes(mission.locationData.routes);
          setCandidateRoutes(mission.locationData.routes);
          setSelectedRouteIndex(mission.locationData.selectedRouteIndex || 0);
          setShowRouteStats(true);
        }

        // Load weather data
        if (mission.weatherData) {
          setWeatherData(mission.weatherData);
        }

        // Load terrain type
        if (mission.locationData.terrainType) {
          setTerrainType(mission.locationData.terrainType);
        }

        // Load analysis data
        if (mission.analysisData) {
          if (mission.analysisData.routeAnalysisData) {
            setRouteAnalysisData(mission.analysisData.routeAnalysisData);
          }
          if (mission.analysisData.terrainAnalysisData) {
            setTerrainAnalysisData(mission.analysisData.terrainAnalysisData);
            setShowTerrainAnalysis(true);
          }
        }

        // Track loaded mission for change detection
        const fingerprint = JSON.stringify({
          name: missionInfo.name,
          startDate: missionInfo.startDate,
          duration: missionInfo.duration,
          selectedVehicle: missionInfo.selectedVehicle,
          startMarker: mission.locationData.startMarker,
          destinationMarker: mission.locationData.destinationMarker,
          routeProfile: missionInfo.routeProfile
        });
        
        setLoadedMissionId(mission.id);
        setLoadedMissionFingerprint(fingerprint);
        
        console.log('✅ Mission loaded successfully:', missionInfo.name);
        console.log('📌 Tracking mission ID for updates:', mission.id);
        console.log('� Fingkerprint created:', fingerprint);
        
        // Clear the global variable
        delete window.loadMissionData;
      } catch (error) {
        console.error('Error loading mission data:', error);
        alert('Failed to load mission data. Some information may be missing.');
      }
    }
  }, []); // Run only on component mount

  const handleTerrainAnalysis = async () => {
    // Validate all required mission planning inputs
    const missingFields = [];
    
    if (!missionName || missionName.trim() === '') {
      missingFields.push('Mission Name');
    }
    if (!startDate) {
      missingFields.push('Start Date');
    }
    if (!duration || duration <= 0) {
      missingFields.push('Duration');
    }
    if (!selectedVehicle) {
      missingFields.push('Vehicle Selection');
    }
    if (!startMarker) {
      missingFields.push('Start Location');
    }
    if (!destinationMarker) {
      missingFields.push('Destination Location');
    }
    
    // Show warning if any fields are missing
    if (missingFields.length > 0) {
      alert(`⚠️ Missing Required Information\n\n` +
            `Please complete the following fields before analyzing:\n\n` +
            missingFields.map(field => `• ${field}`).join('\n') +
            `\n\nAll mission planning fields are required for accurate terrain analysis.`);
      return;
    }
    
    if (!routes || routes.length === 0) {
      alert('Please calculate a route first by clicking "Get Route" button');
      return;
    }
    
    // Open analysis modal
    setShowAnalysisModal(true);
    setAnalysisStage(0);
    setAnalysisElapsed(0);
    setAnalysisSuccess(false);
    setAnalysisError(null);
    setAnalysisRouteCount(routes.length);

    // Always regenerate route analysis when Analyze Mission is clicked
    // This ensures analysis reflects current mission parameters (vehicle, duration, etc.)
    console.log('🔬 Generating real terrain analysis for all routes...');
    setIsLoadingFractal(true);
    setAnalysisStage(1);
    try {
      const analysisPromises = routes.map((route, index) => generateRouteAnalysis(route, index));
      const analysisData = await Promise.all(analysisPromises);
      setRouteAnalysisData(analysisData);
      setAnalysisStage(2);
      console.log(`✅ Got ${routes.length} route(s) with REAL terrain analysis`);
    } catch (analysisError) {
      console.error('❌ Terrain analysis failed:', analysisError);
      setAnalysisError(analysisError.message || 'Terrain analysis failed. Check API configuration.');
      setIsLoadingFractal(false);
      return;
    }
    
    // Perform REAL fractal analysis for SELECTED route only
    if (startMarker && destinationMarker && routes[selectedRouteIndex]) {
      setIsLoadingFractal(true);
      try {
        const selectedRoute = routes[selectedRouteIndex];
        
        // Calculate bbox from route's actual path (not just start/end)
        // This gives route-specific terrain analysis
        const lats = selectedRoute.coordinates.map(c => c.lat);
        const lngs = selectedRoute.coordinates.map(c => c.lng);
        
        const bbox = {
          south: Math.min(...lats) - 0.02,  // Smaller margin for route-specific
          north: Math.max(...lats) + 0.02,
          west: Math.min(...lngs) - 0.02,
          east: Math.max(...lngs) + 0.02
        };
        
        setAnalysisStage(3);
        console.log(`🔬 Analyzing terrain for Route ${selectedRouteIndex + 1}...`);
        
        // Calculate route dimensions
        const latRange = Math.max(...lats) - Math.min(...lats);
        const lngRange = Math.max(...lngs) - Math.min(...lngs);
        
        // For long routes, analyze a representative sample area instead of entire route
        // This keeps the analysis meaningful while staying under 10M point limit
        let analysisBbox;
        
        if (latRange > 0.8 || lngRange > 0.8) {
          // Long route: analyze middle section (most representative)
          console.log(`📊 Long route detected (${latRange.toFixed(2)}° × ${lngRange.toFixed(2)}°) - analyzing representative section`);
          
          const midLat = (Math.max(...lats) + Math.min(...lats)) / 2;
          const midLng = (Math.max(...lngs) + Math.min(...lngs)) / 2;
          
          // Sample area: 0.4° × 0.4° (~44km × 44km) around route midpoint
          const sampleSize = 0.4;
          analysisBbox = {
            south: midLat - sampleSize / 2,
            north: midLat + sampleSize / 2,
            west: midLng - sampleSize / 2,
            east: midLng + sampleSize / 2
          };
          
          console.log(`📏 Sample area: ${sampleSize}° × ${sampleSize}° at route midpoint`);
        } else {
          // Short/medium route: analyze entire route with margin
          const margin = 0.02; // ~2km margin
          analysisBbox = {
            south: Math.min(...lats) - margin,
            north: Math.max(...lats) + margin,
            west: Math.min(...lngs) - margin,
            east: Math.max(...lngs) + margin
          };
          
          console.log(`📏 Full route analysis: ${(latRange + 2*margin).toFixed(3)}° × ${(lngRange + 2*margin).toFixed(3)}°`);
        }
        
        // For fractal analysis, always fetch specific bbox for accuracy
        // Fractal analysis requires a complete grid covering the analysis area
        // For long routes, segmented elevation data may not cover the analysis bbox
        console.log('📊 Fetching elevation data for fractal analysis bbox (ensures accurate box-counting)');
        
        const fractalData = await analyzeTerrain(analysisBbox);
        
        fractalData.routeIndex = selectedRouteIndex;  // Tag with route
        setFractalAnalysisData(fractalData);
        console.log(`✅ Fractal analysis complete for Route ${selectedRouteIndex + 1}`);
        
      } catch (error) {
        console.error('❌ Fractal analysis failed:', error);
        console.warn('⚠️  Fractal analysis unavailable for this route (area too large or API error)');
        // Don't show alert - just log the error
        // Fractal analysis is optional, route analysis already provides terrain metrics
      } finally {
        setIsLoadingFractal(false);
      }
    }

    // Use the analysis data for the selected route if available
    if (routeAnalysisData && routeAnalysisData[selectedRouteIndex]) {
      setTerrainAnalysisData(routeAnalysisData[selectedRouteIndex]);
      setShowTerrainAnalysis(true);
      setAnalysisStage(4);
      setAnalysisSuccess(true);
      await new Promise(r => setTimeout(r, 1800));
      setShowAnalysisModal(false);
      return;
    }

    // Fallback to generating analysis data
    const selectedRoute = routes[selectedRouteIndex];
    const stats = calculateRouteStats(selectedRoute);

    // Calculate terrain parameters
    const elevationGain = selectedRoute.ascent || 0;
    const descent = selectedRoute.descent || 0;
    const maxSlopeValue = Math.round(Math.random() * 35 + 5); // Simulated: 5-40%
    const avgSlopeValue = Math.round(maxSlopeValue * 0.4); // Average slope is typically 40% of max

    // Calculate complexity score based on various factors
    const complexityScore = Math.min(100, Math.round(
      (elevationGain / 50) + // elevation factor
      (maxSlopeValue * 1.5) + // slope factor
      (selectedRoute.distance / 1000 * 0.1) // distance factor
    ));

    const complexityLevel = complexityScore >= 70 ? 'High' : complexityScore >= 40 ? 'Moderate' : 'Low';
    const complexityColor = complexityScore >= 70 ? 'red' : complexityScore >= 40 ? 'yellow' : 'green';

    // Calculate TTI (Terrain Traversability Index)
    const tti = Math.max(0, Math.min(100, Math.round(100 - (complexityScore * 0.7))));

    // Calculate curvature and roughness indices
    const curvatureIndex = Math.round(Math.random() * 60 + 20); // Simulated: 20-80
    const roughnessIndex = Math.round(Math.random() * 70 + 15); // Simulated: 15-85

    // Environmental parameters (enhanced and more realistic)
    const surfaceTypes = ['Paved Road', 'Gravel Road', 'Dirt Track', 'Mixed Terrain', 'Rocky Path', 'Sand Track', 'Mountain Trail'];
    const soilTypes = ['Sandy Loam', 'Clay', 'Silty Loam', 'Rocky', 'Mixed Alluvial', 'Gravelly', 'Compacted Earth'];
    const moistureConditions = ['Arid', 'Dry', 'Moderate', 'Moist', 'Wet'];
    const aspects = ['North', 'Northeast', 'East', 'Southeast', 'South', 'Southwest', 'West', 'Northwest'];

    const surfaceType = surfaceTypes[Math.floor(Math.random() * surfaceTypes.length)];
    const soilType = soilTypes[Math.floor(Math.random() * soilTypes.length)];
    const moisture = moistureConditions[Math.floor(Math.random() * moistureConditions.length)];
    const aspect = aspects[Math.floor(Math.random() * aspects.length)];
    const tempC = Math.round(Math.random() * 25 + 10); // 10-35°C range
    const temperature = isMetric 
      ? `${tempC}°C`
      : `${Math.round(tempC * 9/5 + 32)}°F`;

    // Enhanced terrain parameters
    const maxElevation = Math.round(elevationGain * 1.5 + 1200 + Math.random() * 500);
    const minElevation = Math.round(1200 - descent * 0.8 + Math.random() * 200);
    const steepSections = Math.round(maxSlopeValue / 5 + Math.random() * 10);
    const tractionIndex = Math.max(20, 100 - roughnessIndex + Math.random() * 20);
    const obstacleDensity = Math.round(roughnessIndex * 0.6 + Math.random() * 20);

    // Component health simulation (based on current fleet health patterns)
    const suspensionHealth = Math.round(90 - (roughnessIndex * 0.3) - Math.random() * 10);
    const brakeHealth = Math.round(85 - (maxSlopeValue * 0.5) - Math.random() * 8);
    const engineHealth = Math.round(95 - (complexityScore * 0.2) - Math.random() * 5);
    const transmissionHealth = Math.round(90 - (curvatureIndex * 0.2) - Math.random() * 7);

    // Component wear rates (percentage per mission)
    const suspensionWearRate = Math.round(roughnessIndex * 0.3 + maxSlopeValue * 0.2);
    const brakeWearRate = Math.round(maxSlopeValue * 0.4 + complexityScore * 0.2);
    const engineLoadStress = Math.round(elevationGain * 0.05 + complexityScore * 0.3);
    const transmissionShiftStress = Math.round(curvatureIndex * 0.4 + maxSlopeValue * 0.3);

    // Next service intervals (in km)
    const suspensionNextService = Math.max(100, Math.round(500 - (roughnessIndex * 3)));
    const brakeNextService = Math.max(200, Math.round(800 - (maxSlopeValue * 15)));
    const engineNextService = Math.max(300, Math.round(1200 - (complexityScore * 5)));
    const transmissionNextService = Math.max(250, Math.round(1000 - (curvatureIndex * 8)));

    // Environmental factors
    const soilDensity = `${(1.2 + Math.random() * 0.8).toFixed(1)} g/cm³`;
    const bearingCapacity = `${Math.round(150 + Math.random() * 200)} kPa`;
    const rockContent = `${Math.round(Math.random() * 40)}%`;
    const erosionRisk = Math.round(Math.random() * 100);
    const moisturePercent = `${Math.round(Math.random() * 30 + 10)}%`;
    const vegetationCover = `${Math.round(Math.random() * 60 + 10)}%`;
    const dustFactor = Math.round(Math.random() * 50 + 20);
    const drainageQuality = Math.round(Math.random() * 100);
    const accessibility = Math.round(tti * 0.8 + Math.random() * 15);
    const fractalComplexity = Math.round(complexityScore * 0.8 + Math.random() * 20);

    // Terrain classification
    let terrainClass = 'Moderate Terrain';
    if (elevationGain > 800) terrainClass = 'High Mountain';
    else if (elevationGain > 500) terrainClass = 'Mountainous';
    else if (elevationGain > 200) terrainClass = 'Hilly Terrain';
    else if (roughnessIndex > 70) terrainClass = 'Rough Terrain';
    else if (maxSlopeValue > 20) terrainClass = 'Steep Terrain';

    // Recommended speed based on complexity (unit-aware)
    let recommendedSpeed = getRecommendedSpeed(complexityScore);

    // Calculate average speed based on route duration and distance
    const avgSpeedKmh = selectedRoute.distance && selectedRoute.duration
      ? ((selectedRoute.distance / 1000) / (selectedRoute.duration / 3600)).toFixed(1)
      : '45.0';

    // Get selected vehicle info
    const selectedVehicleInfo = vehicles?.find(v => v.uniqueId === selectedVehicle);

    // Load fuel consumption rate from settings (default: 12 L/100km)
    let baseFuelConsumptionRate = 12;
    if (savedSettings) {
      try {
        const settings = JSON.parse(savedSettings);
        baseFuelConsumptionRate = settings.fleet?.fuelConsumptionRate || 12;
      } catch (error) {
        console.warn('Failed to load fuel consumption rate from settings');
      }
    }

    // Calculate total cargo weight (vehicle capacity + additional cargo)
    const totalCargoWeightLbs = (selectedVehicleInfo?.cargoWeight || 0) + additionalCargoWeight;
    const totalCargoWeightKg = totalCargoWeightLbs * 0.453592;
    
    // Estimate fuel consumption with weight factor (using consistent metric units)
    const weightFactor = totalCargoWeightKg > 0 ? 1 + (totalCargoWeightKg / 5000) : 1; // +10% per 5,000 kg
    const adjustedFuelRate = baseFuelConsumptionRate * weightFactor;
    const fuelEstimate = ((selectedRoute.distance / 1000) * adjustedFuelRate / 100).toFixed(1);
    
    // Format cargo weight with units for display
    const cargoWeight = totalCargoWeightLbs > 0
      ? isMetric 
        ? `${Math.round(totalCargoWeightKg).toLocaleString()} kg`
        : `${Math.round(totalCargoWeightLbs).toLocaleString()} lbs`
      : 'N/A';
    
    const analysisData = {
      missionDetails: {
        missionName: missionName || 'Unnamed Mission',
        startDate: startDate || 'N/A',
        duration: duration ? `${duration} days` : 'N/A',
        vehicle: selectedVehicleInfo ? `${selectedVehicleInfo.brand} ${selectedVehicleInfo.model}` : 'N/A',
        vehicleHealth: selectedVehicleInfo ? selectedVehicleInfo.health : null,
        cargoCapacity: selectedVehicleInfo ? selectedVehicleInfo.cargoWeight : null,
        additionalCargo: additionalCargoWeight,
        vehicleMileage: selectedVehicleInfo ? selectedVehicleInfo.mileage : null,
        vehicleYear: selectedVehicleInfo ? selectedVehicleInfo.year : null,
        totalDistance: stats.distance,
        estimatedTime: stats.duration,
        avgSpeed: `${avgSpeedKmh} km/h`,
        fuelEstimate: `${fuelEstimate} L`,
        cargoWeight: cargoWeight
      },
      terrainParameters: {
        // Basic parameters
        surfaceType: surfaceType,
        soilType: soilType,
        moisture: moisture,
        temperature: temperature,
        aspect: aspect,

        // Complexity and traversability
        complexity: {
          level: complexityLevel,
          score: complexityScore,
          color: complexityColor
        },
        tti: tti,

        // Elevation data
        elevationGain: elevationGain,
        descent: descent,
        maxElevation: maxElevation,
        minElevation: minElevation,

        // Slope analysis
        maxSlope: maxSlopeValue,
        avgSlope: avgSlopeValue,
        steepSections: steepSections,

        // Surface conditions
        curvatureIndex: curvatureIndex,
        roughnessIndex: roughnessIndex,
        tractionIndex: Math.round(tractionIndex),
        obstacleDensity: obstacleDensity,

        // Environmental factors
        soilDensity: soilDensity,
        bearingCapacity: bearingCapacity,
        rockContent: rockContent,
        erosionRisk: erosionRisk,
        moisturePercent: moisturePercent,
        vegetationCover: vegetationCover,
        dustFactor: dustFactor,
        drainageQuality: drainageQuality,
        accessibility: accessibility,
        fractalComplexity: fractalComplexity,

        // Classification
        terrainClass: terrainClass,
        recommendedSpeed: recommendedSpeed,

        // Component health data
        suspensionHealth: suspensionHealth,
        brakeHealth: brakeHealth,
        engineHealth: engineHealth,
        transmissionHealth: transmissionHealth,

        // Component wear rates
        suspensionWearRate: suspensionWearRate,
        brakeWearRate: brakeWearRate,
        engineLoadStress: engineLoadStress,
        transmissionShiftStress: transmissionShiftStress,

        // Service intervals
        suspensionNextService: suspensionNextService,
        brakeNextService: brakeNextService,
        engineNextService: engineNextService,
        transmissionNextService: transmissionNextService
      }
    };

    setTerrainAnalysisData(analysisData);
    setShowTerrainAnalysis(true);
    setAnalysisStage(4);
    setAnalysisSuccess(true);
    await new Promise(r => setTimeout(r, 1800));
    setShowAnalysisModal(false);
  };


  // Get stress color based on stress level
  const getStressColor = (stressLevel) => {
    switch (stressLevel) {
      case 'high': return '#dc2626'; // Red
      case 'medium': return '#f59e0b'; // Orange/Yellow
      case 'low': return '#16a34a'; // Green
      default: return '#6b7280'; // Gray
    }
  };

  const handleClearPins = () => {
    setStartMarker(null);
    setDestinationMarker(null);
    setStartLocation('');
    setDestinationLocation('');
    setStartLocationName('');
    setDestinationLocationName('');
    setRoutes([]);
    setCandidateRoutes([]);
    setRouteAnalysisData([]);
    setShowRouteStats(false);
    setWeatherData(null);
    setTerrainAnalysisData(null);
    setShowTerrainAnalysis(false);
  };

  const handleResetMission = () => {
    if (window.confirm('Are you sure you want to reset the mission? All data will be cleared.')) {
      // Clear all mission data
      handleClearPins();
      setMissionName('');
      setStartDate('');
      setDuration('');
      setSelectedVehicle('');
      setAdditionalCargoWeight(0);
      setWeatherData(null);
      setCandidateRoutes([]);
      setRouteAnalysisData([]);
      setRoutes([]);
      setShowRouteStats(false);
      setTerrainAnalysisData(null);
      setShowTerrainAnalysis(false);
      setFractalAnalysisData(null);
      setStartLocation('');
      setDestinationLocation('');
      setStartLocationName('');
      setDestinationLocationName('');
    }
  };

  // Create fingerprint of current mission data for change detection
  const createMissionFingerprint = () => {
    return JSON.stringify({
      name: missionName,
      startDate: startDate,
      duration: duration,
      selectedVehicle: selectedVehicle,
      startMarker: startMarker,
      destinationMarker: destinationMarker,
      routeProfile: routeProfile
    });
  };

  // Check if mission has been modified
  const hasMissionChanged = () => {
    if (!loadedMissionId || !loadedMissionFingerprint) {
      return true; // New mission, always save
    }
    
    const currentFingerprint = createMissionFingerprint();
    return currentFingerprint !== loadedMissionFingerprint;
  };

  // Handle ML DEM enhancement
  // Tick elapsed seconds while the ML enhancement modal is open
  useEffect(() => {
    let interval;
    if (showEnhancementModal && isEnhancing) {
      interval = setInterval(() => setEnhancementElapsed(prev => prev + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [showEnhancementModal, isEnhancing]);

  // Tick elapsed seconds while the analysis modal is open
  useEffect(() => {
    let interval;
    if (showAnalysisModal && !analysisSuccess && !analysisError) {
      interval = setInterval(() => setAnalysisElapsed(prev => prev + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [showAnalysisModal, analysisSuccess, analysisError]);

  const getEstimatedDuration = (gridInfo) => {
    if (!gridInfo) return 60;
    const pixels = (gridInfo.rows || 0) * (gridInfo.cols || 0);
    if (pixels < 200000) return 25;
    if (pixels < 400000) return 45;
    return 220;
  };

  const getEnhancementStage = (elapsed, gridInfo) => {
    const est = getEstimatedDuration(gridInfo);
    const pct = est > 0 ? elapsed / est : 0;
    if (pct < 0.05) return 0;
    if (pct < 0.12) return 1;
    if (pct < 0.88) return 2;
    return 3;
  };

  const formatElapsed = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const handleEnhanceDEM = async () => {
    if (!routeAnalysisData || routeAnalysisData.length === 0) {
      alert('Please analyze a route first before enhancing');
      return;
    }

    // Get the selected route analysis
    const selectedAnalysis = routeAnalysisData[selectedRouteIndex];
    if (!selectedAnalysis) {
      alert('No route analysis available');
      return;
    }

    console.log('🔍 DEBUG: Selected analysis data structure:');
    console.log('   Keys:', Object.keys(selectedAnalysis));
    console.log('   Has elevationData:', 'elevationData' in selectedAnalysis);
    console.log('   elevationData value:', selectedAnalysis.elevationData);
    console.log('   elevationData type:', typeof selectedAnalysis.elevationData);
    console.log('   elevationData is null:', selectedAnalysis.elevationData === null);
    console.log('   Has rawElevationData:', 'rawElevationData' in selectedAnalysis);
    console.log('   rawElevationData value:', selectedAnalysis.rawElevationData);

    setIsEnhancing(true);
    setEnhancementError(null);
    setShowEnhancedComparison(false);
    setEnhancementSuccess(false);
    setEnhancementElapsed(0);
    setEnhancementGridInfo(null);
    setShowEnhancementModal(true);

    try {
      console.log('🔬 Starting ML enhancement...');
      console.log('🔍 DEBUG: selectedAnalysis object:', selectedAnalysis);
      console.log('🔍 DEBUG: elevationData value:', selectedAnalysis.elevationData);
      console.log('🔍 DEBUG: elevationData type:', typeof selectedAnalysis.elevationData);

      // Get the route data
      const route = routes[selectedRouteIndex];
      if (!route) {
        throw new Error('Route data not found');
      }

      // Get elevation data from the route analysis
      // The backend needs the 30m DEM data that was used for the original analysis
      const elevationData = selectedAnalysis.elevationData || selectedAnalysis.rawElevationData;
      
      if (!elevationData) {
        console.error('❌ No elevation data found in analysis');
        console.error('   Analysis keys:', Object.keys(selectedAnalysis));
        console.error('   elevationData actual value:', selectedAnalysis.elevationData);
        console.error('   rawElevationData actual value:', selectedAnalysis.rawElevationData);
        console.error('   terrainParameters keys:', Object.keys(selectedAnalysis.terrainParameters || {}));
        throw new Error('No elevation data available. Please re-analyze the route.');
      }

      console.log('✅ Elevation data found!');
      console.log('   Source:', elevationData.source || 'unknown source');
      console.log('   Grid size:', elevationData.grid?.length, 'x', elevationData.grid?.[0]?.length);
      setEnhancementGridInfo({
        rows: elevationData.grid?.length || 0,
        cols: elevationData.grid?.[0]?.length || 0,
      });

      // Get vehicle data
      const selectedVehicleData = vehicles?.find(v => v.uniqueId === selectedVehicle);
      const vehicleData = selectedVehicleData ? {
        cargoWeight: (selectedVehicleData.cargoWeight || 0) + additionalCargoWeight,
        componentHealth: selectedVehicleData.components || {
          engine: 92,
          suspension: 90,
          brakes: 85,
          transmission: 90
        }
      } : null;

      // Call ML enhancement API
      const result = await enhanceAndAnalyze(route, elevationData, vehicleData);

      if (result.success) {
        console.log('✅ ML enhancement successful');
        console.log('   Source:', result.source);
        console.log('   Cache hit:', result.cacheHit);

        // Store enhanced analysis - use backend-provided 30m analysis for accurate comparison
        setEnhancedAnalysis({
          analysis10m: result.data.analysis10m,
          analysis30m: result.data.analysis30m, // Use fresh 30m analysis from backend
          enhancement: result.data.enhancement,
          stats: result.data.stats
        });

        setEnhancementSuccess(true);
        await new Promise(r => setTimeout(r, 2200));
        setShowEnhancementModal(false);
        setShowEnhancedComparison(true);
      } else {
        throw new Error(result.message || 'Enhancement failed');
      }

    } catch (error) {
      console.error('❌ ML enhancement failed:', error);
      setEnhancementError(error.message || 'Enhancement failed. Please try again.');
      // Error is shown inside the modal — no alert needed
    } finally {
      setIsEnhancing(false);
    }
  };

  // Handle saving mission to Saved Missions
  const handleSaveMission = async () => {
    if (!routes.length || !terrainAnalysisData) {
      alert('Please analyze the mission first before saving');
      return;
    }

    console.log('🔍 Save Mission Check:', {
      loadedMissionId,
      hasLoadedMissionId: !!loadedMissionId,
      loadedMissionFingerprint: loadedMissionFingerprint ? 'exists' : 'null',
      currentFingerprint: createMissionFingerprint()
    });

    // Check if this is a loaded mission and if it has changed
    if (loadedMissionId) {
      console.log('📊 Change Detection:', {
        missionHasChanges,
        originalFingerprint: loadedMissionFingerprint,
        currentFingerprint: createMissionFingerprint()
      });
      
      if (!missionHasChanges) {
        // No changes detected
        alert('ℹ️ No Changes Detected\n\n' +
              'This mission has not been modified since loading.\n' +
              'No need to save again.');
        return;
      } else {
        // Changes detected - show options modal
        setShowSaveOptionsModal(true);
        return;
      }
    }
    
    console.log('💾 New mission - saving directly');
    // New mission - proceed with normal save
    await performSave(false);
  };

  // Perform the actual save operation
  const performSave = async (isUpdate = false) => {

    if (!missionName.trim()) {
      alert('Please enter a mission name before saving');
      return;
    }

    try {
      // Get selected vehicle info
      const selectedVehicleInfo = vehicles?.find(v => v.uniqueId === selectedVehicle);

      console.log('=== SAVE MISSION DEBUG ===');
      console.log('Selected Vehicle ID:', selectedVehicle);
      console.log('Available Vehicles:', vehicles?.map(v => ({ uniqueId: v.uniqueId, brand: v.brand, model: v.model })));
      console.log('Found Vehicle Object:', selectedVehicleInfo);
      
      if (!selectedVehicle || !selectedVehicleInfo) {
        console.warn('⚠️ No vehicle selected or vehicle not found. Mission will be saved without vehicle info.');
      }

      // Prepare mission info
      console.log('📦 Additional Cargo Weight at save time:', additionalCargoWeight, 'Type:', typeof additionalCargoWeight);
      
      const missionInfo = {
        missionName: missionName.trim(),
        startDate: startDate,
        duration: duration,
        selectedVehicle: selectedVehicle,
        vehicleInfo: selectedVehicleInfo || null,
        additionalCargoWeight: additionalCargoWeight,
        routeProfile: routeProfile,
        startMarker: startMarker,
        destinationMarker: destinationMarker,
        startLocationName: startLocationName,
        destinationLocationName: destinationLocationName,
        routes: routes,
        selectedRouteIndex: selectedRouteIndex,
        terrainType: terrainType,
        weatherData: weatherData
      };
      
      console.log('Mission Info with Vehicle:', missionInfo);
      console.log('========================');

      // Prepare analysis data in the expected format
      const analysisData = {
        cardA: {
          totalDistance: routeAnalysisData[selectedRouteIndex]?.missionDetails.totalDistance || '0 km',
          estimatedDuration: routeAnalysisData[selectedRouteIndex]?.missionDetails.estimatedTime || '0h 0m',
          fuelEstimate: routeAnalysisData[selectedRouteIndex]?.missionDetails.fuelEstimate || '0 L'
        },
        cardE: {
          missionComplexityScore: routeAnalysisData[selectedRouteIndex]?.terrainParameters.complexity.score || 0,
          overallComplexity: {
            level: routeAnalysisData[selectedRouteIndex]?.terrainParameters.complexity.level || 'Low'
          }
        },
        routeAnalysisData: routeAnalysisData,
        terrainAnalysisData: terrainAnalysisData
      };

      // Calculate predicted health from component analysis
      let predictedHealth = null;
      const componentStress = routeAnalysisData[selectedRouteIndex]?.componentStress;
      if (componentStress && selectedVehicleInfo) {
        // Calculate average post-mission health from all components
        const components = ['engine', 'brakes', 'suspension', 'transmission'];
        const postMissionHealthValues = components
          .map(comp => componentStress[comp]?.postMissionHealth)
          .filter(val => val !== undefined);
        
        if (postMissionHealthValues.length > 0) {
          predictedHealth = Math.round(
            postMissionHealthValues.reduce((sum, val) => sum + val, 0) / postMissionHealthValues.length
          );
          console.log('📊 Calculated predicted health:', predictedHealth, 'from components:', postMissionHealthValues);
        }
      }

      // Create mission data
      const missionData = createMissionData(missionInfo, analysisData);
      
      // Add predicted health to mission data
      if (predictedHealth !== null) {
        missionData.predictedHealth = predictedHealth;
      }
      
      // If updating, preserve the original mission ID
      if (isUpdate && loadedMissionId) {
        missionData.id = loadedMissionId;
      }
      
      // Save or update mission
      let result;
      if (isUpdate && loadedMissionId) {
        // Update existing mission
        const { updateMissionMetadata } = await import('../utils/missionStorageAPI');
        
        // If in reroute mode, set status back to "planned" and track reroute event
        if (rerouteMode) {
          missionData.status = 'planned';
          
          // Track reroute event
          const existingReroutes = missionData.reroutes || [];
          const rerouteEvent = {
            id: `reroute_${Date.now()}`,
            timestamp: new Date().toISOString(),
            originalRoute: originalRouteData,
            newRoute: {
              startMarker: startMarker,
              destinationMarker: destinationMarker,
              startLocationName: startLocationName,
              destinationLocationName: destinationLocationName,
              routes: routes,
              selectedRouteIndex: selectedRouteIndex
            }
          };
          missionData.reroutes = [...existingReroutes, rerouteEvent];
          
          console.log('🔄 Reroute tracked:', rerouteEvent);
        }
        
        result = await updateMissionMetadata(loadedMissionId, missionData);
      } else {
        // Create new mission
        result = await saveMission(missionData);
        
        // If this was a loaded mission saved as new, clear the loaded mission tracking
        if (loadedMissionId) {
          setLoadedMissionId(null);
          setLoadedMissionFingerprint(null);
        }
      }

      if (result.success) {
        // Update fingerprint after successful save
        if (isUpdate) {
          setLoadedMissionFingerprint(createMissionFingerprint());
          
          if (rerouteMode) {
            alert(`✅ Mission Rerouted!\n\n"${missionName}" has been updated with the new route.\n\nStatus: Planned (must be re-applied to vehicle)`);
            // Exit reroute mode
            setRerouteMode(false);
            setOriginalRouteData(null);
          } else {
            alert(`✅ Mission Updated!\n\n"${missionName}" has been updated successfully.`);
          }
        } else {
          if (result.warning) {
            alert(`✅ Mission Saved!\n\n"${missionName}" saved successfully!\n\nNote: ${result.warning}`);
          } else {
            alert(`✅ Mission Saved!\n\n"${missionName}" saved successfully!`);
          }
        }
        
        // Close modal if open
        setShowSaveOptionsModal(false);
      } else {
        alert(`❌ Failed to save mission:\n\n${result.error}`);
      }
    } catch (error) {
      console.error('Error saving mission:', error);
      alert('Failed to save mission. Please try again.');
    }
  };



  // Handle start location input change
  const handleStartLocationChange = async (value) => {
    setStartLocation(value);

    if (inputMode === 'address' && value.length >= 3) {
      const suggestions = await searchAddresses(value);
      setStartSuggestions(suggestions);
      setShowStartSuggestions(true);
    } else {
      setShowStartSuggestions(false);
    }
  };

  // Handle destination location input change
  const handleDestinationLocationChange = async (value) => {
    setDestinationLocation(value);

    if (inputMode === 'address' && value.length >= 3) {
      const suggestions = await searchAddresses(value);
      setDestinationSuggestions(suggestions);
      setShowDestinationSuggestions(true);
    } else {
      setShowDestinationSuggestions(false);
    }
  };

  // Select start location from suggestions
  const handleSelectStartSuggestion = (suggestion) => {
    setStartLocation(suggestion.display_name);
    setStartLocationName(suggestion.display_name);
    setStartMarker({ lat: suggestion.lat, lng: suggestion.lng });
    setShowStartSuggestions(false);
  };

  // Select destination location from suggestions
  const handleSelectDestinationSuggestion = (suggestion) => {
    setDestinationLocation(suggestion.display_name);
    setDestinationLocationName(suggestion.display_name);
    setDestinationMarker({ lat: suggestion.lat, lng: suggestion.lng });
    setShowDestinationSuggestions(false);
  };

  // Handle geocoding when user presses Enter or clicks a button
  const handleGeocodeStart = async () => {
    if (inputMode === 'coordinates') {
      const coords = parseCoordinates(startLocation);
      if (coords) {
        setStartMarker(coords);
        // Get location name via reverse geocoding
        try {
          const address = await reverseGeocode(coords.lat, coords.lng);
          setStartLocationName(address.display_name);
        } catch (error) {
          console.error('Reverse geocoding failed:', error);
        }
      } else {
        alert('Invalid coordinate format. Please use: latitude, longitude');
      }
    } else if (inputMode === 'address') {
      try {
        const results = await geocodeAddress(startLocation);
        if (results && results.length > 0) {
          const result = results[0];
          setStartMarker({ lat: result.lat, lng: result.lng });
          setStartLocationName(result.display_name);
        }
      } catch (error) {
        alert('Address not found. Please try a different address.');
      }
    }
  };

  const handleGeocodeDestination = async () => {
    if (inputMode === 'coordinates') {
      const coords = parseCoordinates(destinationLocation);
      if (coords) {
        setDestinationMarker(coords);
        // Get location name via reverse geocoding
        try {
          const address = await reverseGeocode(coords.lat, coords.lng);
          setDestinationLocationName(address.display_name);
        } catch (error) {
          console.error('Reverse geocoding failed:', error);
        }
      } else {
        alert('Invalid coordinate format. Please use: latitude, longitude');
      }
    } else if (inputMode === 'address') {
      try {
        const results = await geocodeAddress(destinationLocation);
        if (results && results.length > 0) {
          const result = results[0];
          setDestinationMarker({ lat: result.lat, lng: result.lng });
          setDestinationLocationName(result.display_name);
        }
      } catch (error) {
        alert('Address not found. Please try a different address.');
      }
    }
  };

  // Reverse geocode when pins are placed in pin mode
  useEffect(() => {
    if (inputMode === 'pin' && startMarker && !startLocationName) {
      reverseGeocode(startMarker.lat, startMarker.lng)
        .then(address => setStartLocationName(address.display_name))
        .catch(console.error);
    }
  }, [startMarker, inputMode]);

  useEffect(() => {
    if (inputMode === 'pin' && destinationMarker && !destinationLocationName) {
      reverseGeocode(destinationMarker.lat, destinationMarker.lng)
        .then(address => setDestinationLocationName(address.display_name))
        .catch(console.error);
    }
  }, [destinationMarker, inputMode]);

  return (
    <div className="p-3 sm:p-4 md:p-6 max-w-[1600px] mx-auto overflow-x-hidden">
      {/* Header */}
      <div className="mb-6">
        <h2 className={`text-3xl font-bold ${themeClasses.textPrimary}`}>Terrain Intelligence</h2>
        <p className={`${themeClasses.textMuted} mt-1`}>Mission planning and terrain analysis</p>
      </div>

      {/* Mission Planning Inputs */}
      <div className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-4 mb-6`}>
        <h3 className={`text-lg font-bold ${themeClasses.textPrimary} mb-3`}>Mission Planning</h3>

        {/* First Row: Mission Info */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3">
          {/* Mission Name */}
          <div>
            <label className={`block ${themeClasses.textSecondary} text-xs font-medium mb-1`}>
              Mission Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={missionName}
              onChange={(e) => setMissionName(e.target.value)}
              placeholder="Enter mission name"
              className={`w-full px-3 py-1.5 text-sm ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} placeholder-gray-500 ${themeClasses.borderFocus} focus:outline-none`}
            />
          </div>

          {/* Start Date */}
          <div>
            <label className={`block ${themeClasses.textSecondary} text-xs font-medium mb-1`}>
              <Calendar className="inline mr-1" size={12} />
              Start Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className={`w-full px-3 py-1.5 text-sm ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
            />
          </div>

          {/* Duration */}
          <div>
            <label className={`block ${themeClasses.textSecondary} text-xs font-medium mb-1`}>
              <Clock className="inline mr-1" size={12} />
              Duration (days) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="Enter days"
              min="1"
              className={`w-full px-3 py-1.5 text-sm ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} placeholder-gray-500 ${themeClasses.borderFocus} focus:outline-none`}
            />
          </div>

          {/* Vehicle Selection */}
          <div>
            <label className={`block ${themeClasses.textSecondary} text-xs font-medium mb-1`}>
              <Truck className="inline mr-1" size={12} />
              Select Vehicle <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedVehicle}
              onChange={(e) => setSelectedVehicle(e.target.value)}
              className={`w-full px-3 py-1.5 text-sm ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
            >
              <option value="">Select Prime Mover or Armament Carrier</option>
              {vehicles && vehicles.map((vehicle) => (
                <option key={vehicle.uniqueId} value={vehicle.uniqueId}>
                  {vehicle.brand} {vehicle.model} ({vehicle.id}) - {vehicle.health}% Health - {vehicle.baseInfo?.baseName}, {vehicle.baseInfo?.countryName}
                </option>
              ))}
            </select>
          </div>

          {/* Additional Cargo Weight */}
          <div>
            <label className={`block ${themeClasses.textSecondary} text-xs font-medium mb-1`}>
              <Package className="inline mr-1" size={12} />
              Additional Cargo (lbs)
            </label>
            <input
              type="number"
              min="0"
              step="100"
              value={additionalCargoWeight || 0}
              onChange={(e) => setAdditionalCargoWeight(Number(e.target.value) || 0)}
              placeholder="Enter cargo"
              className={`w-full px-3 py-1.5 text-sm ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
            />
            {selectedVehicle && vehicles && (() => {
              const vehicleCapacity = vehicles.find(v => v.uniqueId === selectedVehicle)?.cargoWeight;
              const displayCapacity = vehicleCapacity
                ? isMetric
                  ? Math.round(vehicleCapacity * 0.453592)
                  : vehicleCapacity
                : 'N/A';
              return (
                <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
                  Capacity: {displayCapacity !== 'N/A' ? displayCapacity.toLocaleString() : displayCapacity} {weightUnit}
                </p>
              );
            })()}
          </div>
        </div>

        {/* Second Row: Route Planning */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Input Mode */}
          <div>
            <label className={`block ${themeClasses.textSecondary} text-xs font-medium mb-1`}>Input Mode</label>
            <select
              value={inputMode}
              onChange={(e) => setInputMode(e.target.value)}
              className={`w-full px-3 py-1.5 text-sm ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
            >
              <option value="address">Address</option>
              <option value="coordinates">Coordinates</option>
              <option value="pin">Pin on Map</option>
            </select>
          </div>

          {/* Start Location */}
          <div className="relative">
            <label className={`block ${themeClasses.textSecondary} text-xs font-medium mb-1`}>
              <MapPin className="inline mr-1 text-green-500" size={12} />
              Start Location <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={startLocation}
                onChange={(e) => handleStartLocationChange(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleGeocodeStart()}
                placeholder={inputMode === 'coordinates' ? 'Lat, Lng' : 'Enter address'}
                disabled={inputMode === 'pin'}
                className={`flex-1 px-3 py-1.5 text-sm ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} placeholder-gray-500 ${themeClasses.borderFocus} focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed`}
              />
              {inputMode !== 'pin' && (
                <button
                  onClick={handleGeocodeStart}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  title="Search location"
                >
                  <Search size={16} />
                </button>
              )}
            </div>

            {/* Address Suggestions Dropdown */}
            {showStartSuggestions && startSuggestions.length > 0 && (
              <div className={`absolute z-10 w-full mt-1 ${themeClasses.card} border ${themeClasses.border} rounded-lg shadow-xl max-h-60 overflow-y-auto`}>
                {startSuggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    onClick={() => handleSelectStartSuggestion(suggestion)}
                    className={`px-4 py-2 hover:${themeClasses.secondary} cursor-pointer ${themeClasses.textPrimary} text-sm border-b ${themeClasses.border} last:border-b-0`}
                  >
                    {suggestion.display_name}
                  </div>
                ))}
              </div>
            )}

            {/* Location Name Display */}
            {startLocationName && (
              <p className="mt-1 text-xs text-green-400">
                📍 {startLocationName}
              </p>
            )}
          </div>

          {/* Destination Location */}
          <div className="relative">
            <label className={`block ${themeClasses.textSecondary} text-xs font-medium mb-1`}>
              <Navigation className="inline mr-1 text-red-500" size={12} />
              Destination <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={destinationLocation}
                onChange={(e) => handleDestinationLocationChange(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleGeocodeDestination()}
                placeholder={inputMode === 'coordinates' ? 'Lat, Lng' : 'Enter address'}
                disabled={inputMode === 'pin'}
                className={`flex-1 px-3 py-1.5 text-sm ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} placeholder-gray-500 ${themeClasses.borderFocus} focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed`}
              />
              {inputMode !== 'pin' && (
                <button
                  onClick={handleGeocodeDestination}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  title="Search location"
                >
                  <Search size={16} />
                </button>
              )}
            </div>

            {/* Address Suggestions Dropdown */}
            {showDestinationSuggestions && destinationSuggestions.length > 0 && (
              <div className={`absolute z-10 w-full mt-1 ${themeClasses.card} border ${themeClasses.border} rounded-lg shadow-xl max-h-60 overflow-y-auto`}>
                {destinationSuggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    onClick={() => handleSelectDestinationSuggestion(suggestion)}
                    className={`px-4 py-2 hover:${themeClasses.secondary} cursor-pointer ${themeClasses.textPrimary} text-sm border-b ${themeClasses.border} last:border-b-0`}
                  >
                    {suggestion.display_name}
                  </div>
                ))}
              </div>
            )}

            {/* Location Name Display */}
            {destinationLocationName && (
              <p className="mt-1 text-xs text-red-400">
                📍 {destinationLocationName}
              </p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-4">
          <button
            onClick={handleTerrainAnalysis}
            disabled={!routes.length}
            className={`px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:${themeClasses.buttonSecondary} disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors`}
          >
            {loadedMissionId && missionHasChanges ? 'Re-Analyze Mission' : 'Analyze Mission'}
          </button>
          <button
            onClick={handleResetMission}
            className="px-4 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Reset Mission
          </button>
        </div>
      </div>

      {/* Map Section */}
      <div className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-6`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-xl font-bold ${themeClasses.textPrimary}`}>Mission Route Map</h3>

          <div className="flex items-center gap-6">
            {/* GPS Location Button */}
            <button
              onClick={handleGetCurrentLocation}
              disabled={isGettingLocation}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                isGettingLocation
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
              title="Get Current Location"
            >
              <Crosshair size={18} className={isGettingLocation ? 'animate-spin' : ''} />
              {isGettingLocation ? 'Getting Location...' : 'Use My Location'}
            </button>

            {/* Map Type Selector */}
            <div className="flex gap-2">
              <button
                onClick={() => setMapType('terrain')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${mapType === 'terrain'
                  ? 'bg-blue-600 text-white'
                  : `${themeClasses.secondary} ${themeClasses.textMuted} hover:${themeClasses.card}`
                  }`}
              >
                Terrain
              </button>
              <button
                onClick={() => setMapType('satellite')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${mapType === 'satellite'
                  ? 'bg-blue-600 text-white'
                  : `${themeClasses.secondary} ${themeClasses.textMuted} hover:${themeClasses.card}`
                  }`}
              >
                Satellite
              </button>
              <button
                onClick={() => setMapType('topography')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${mapType === 'topography'
                  ? 'bg-blue-600 text-white'
                  : `${themeClasses.secondary} ${themeClasses.textMuted} hover:${themeClasses.card}`
                  }`}
              >
                Topography
              </button>
            </div>
          </div>
        </div>

        {/* Leaflet Map */}
        <div className={`h-[600px] rounded-lg overflow-hidden border ${themeClasses.border}`} style={{ position: 'relative' }}>
          <MapContainer
            center={defaultCenter}
            zoom={defaultZoom}
            style={{ height: '600px', width: '100%', position: 'absolute', top: 0, left: 0 }}
            scrollWheelZoom={true}
            wheelPxPerZoomLevel={120}
            wheelDebounceTime={40}
            zoomDelta={0.5}
            zoomSnap={0.5}
            zoomControl={true}
            attributionControl={true}
            worldCopyJump={false}
            maxBounds={[[-90, -180], [90, 180]]}
            maxBoundsViscosity={1.0}
            whenReady={(map) => {
              console.log('✅ Map is ready and initialized');
              console.log('Map center:', map.target.getCenter());
              console.log('Map zoom:', map.target.getZoom());
              console.log('🌍 Map bounds restricted to prevent coordinate wrapping');
              
              // Store map instance for GPS centering
              setMapInstance(map.target);
              
              // Force map to invalidate size multiple times to ensure proper rendering
              map.target.invalidateSize();
              
              setTimeout(() => {
                map.target.invalidateSize();
                console.log('🗺️ Map size invalidated (100ms)');
              }, 100);
              
              setTimeout(() => {
                map.target.invalidateSize();
                console.log('🗺️ Map size invalidated (300ms)');
              }, 300);
            }}
          >
            <TileLayer
              key={mapType}
              attribution={tileConfigs[mapType].attribution}
              url={tileConfigs[mapType].url}
              maxZoom={tileConfigs[mapType].maxZoom}
              minZoom={2}
              tileSize={256}
              keepBuffer={4}
              eventHandlers={{
                tileerror: (error) => {
                  console.error('❌ Tile loading error:', error);
                  console.error('Failed tile URL:', error.tile?.src);
                }
              }}
            />

            {/* Map Diagnostics */}
            <MapDiagnostics />

            {/* Map resizer - fixes tile loading on tab switch */}
            <MapResizer />

            {/* Map click handler for pin mode */}
            <MapClickHandler
              inputMode={inputMode}
              startMarker={startMarker}
              setStartMarker={setStartMarker}
              destinationMarker={destinationMarker}
              setDestinationMarker={setDestinationMarker}
              onClearRoutes={() => {
                setRoutes([]);
                setCandidateRoutes([]);
                setRouteAnalysisData([]);
                setShowRouteStats(false);
                setFractalAnalysisData(null);
                setTerrainAnalysisData(null);
                console.log('🔄 New pin placed - routes cleared');
              }}
            />

            {/* Original Route Visualization (Reroute Mode) */}
            {rerouteMode && originalRouteData && (
              <>
                {/* Original Start Marker - Pale Green */}
                <Marker
                  position={originalRouteData.startMarker}
                  icon={paleGreenMarkerIcon}
                  opacity={0.5}
                  draggable={false}
                  zIndexOffset={-1000}
                >
                  <Popup>
                    <div className="text-sm">
                      <strong>Original Start</strong><br />
                      📍 {originalRouteData.startLocationName}<br />
                      Lat: {originalRouteData.startMarker.lat.toFixed(4)}<br />
                      Lng: {originalRouteData.startMarker.lng.toFixed(4)}
                    </div>
                  </Popup>
                </Marker>
                
                {/* Original Destination Marker - Pale Red */}
                <Marker
                  position={originalRouteData.destinationMarker}
                  icon={paleRedMarkerIcon}
                  opacity={0.5}
                  draggable={false}
                  zIndexOffset={-1000}
                >
                  <Popup>
                    <div className="text-sm">
                      <strong>Original Destination</strong><br />
                      📍 {originalRouteData.destinationLocationName}<br />
                      Lat: {originalRouteData.destinationMarker.lat.toFixed(4)}<br />
                      Lng: {originalRouteData.destinationMarker.lng.toFixed(4)}
                    </div>
                  </Popup>
                </Marker>
                
                {/* Original Route Polyline - Gray Dashed */}
                {originalRouteData.routes && originalRouteData.routes.length > 0 && (
                  <Polyline
                    positions={originalRouteData.routes[0].coordinates.map(coord => {
                      let lng = coord.lng;
                      while (lng > 180) lng -= 360;
                      while (lng < -180) lng += 360;
                      return [coord.lat, lng];
                    })}
                    pathOptions={{
                      color: '#0a7e8dff',      // Gray (gray-400) - more visible
                      weight: 3,
                      opacity: 0.8,
                      dashArray: '10, 10'    // Dashed
                    }}
                  >
                    <Popup>
                      <div className="text-sm">
                        <strong>Original Route</strong><br />
                        Distance: {(originalRouteData.routes[0].distance / 1000).toFixed(2)} km<br />
                        <span className="text-gray-500">This route will be replaced</span>
                      </div>
                    </Popup>
                  </Polyline>
                )}
              </>
            )}

            {/* Draw Routes with Matching Colors */}
            {routes.map((route, index) => {
              const analysis = routeAnalysisData[index];
              // Use the same color as in the route cards
              const routeColor = analysis ? analysis.routeColor : getRouteColor(index);

              // Normalize route coordinates to prevent wrapping issues
              const normalizedPositions = route.coordinates.map(coord => {
                let lng = coord.lng;
                while (lng > 180) lng -= 360;
                while (lng < -180) lng += 360;
                return [coord.lat, lng];
              });

              return (
                <Polyline
                  key={index}
                  positions={normalizedPositions}
                  color={routeColor}
                  weight={index === selectedRouteIndex ? 8 : 5}
                  opacity={index === selectedRouteIndex ? 0.9 : 0.7}
                  dashArray={index === 0 ? null : '10, 5'}
                  eventHandlers={{
                    click: () => setSelectedRouteIndex(index),
                  }}
                >
                  <Popup>
                    <div className="text-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: routeColor }}
                        ></div>
                        <strong>
                          {analysis ? analysis.routeType : (index === 0 ? 'Main Route' : `Alternative ${index}`)}
                        </strong>
                      </div>
                      {route.routeType && (
                        <>
                          <strong>Type:</strong> {route.routeType.charAt(0).toUpperCase() + route.routeType.slice(1)}<br />
                        </>
                      )}
                      <strong>Distance:</strong> {(route.distance / 1000).toFixed(2)} km<br />
                      <strong>Duration:</strong> {Math.floor(route.duration / 3600)}h {Math.floor((route.duration % 3600) / 60)}m<br />
                      <strong>Ascent:</strong> {route.ascent}m<br />
                      <strong>Descent:</strong> {route.descent}m<br />
                      {analysis && (
                        <>
                          <strong>Maintenance Priority:</strong> {analysis.terrainParameters.maintenancePriorityScore}/100<br />
                          <strong>TTI Score:</strong> {analysis.terrainParameters.tti}/100<br />
                          <strong>Terrain:</strong> {analysis.terrainParameters.terrainClass}
                        </>
                      )}
                    </div>
                  </Popup>
                </Polyline>
              );
            })}

            {/* Start Marker */}
            {startMarker && (
              <Marker
                position={startMarker}
                icon={startIcon}
                draggable={true}
                eventHandlers={{
                  dragend: (e) => {
                    setStartMarker(e.target.getLatLng());
                    setStartLocationName(''); // Reset name when dragged
                    // Clear routes and analysis when marker is moved
                    setRoutes([]);
                    setCandidateRoutes([]);
                    setRouteAnalysisData([]);
                    setShowRouteStats(false);
                    setFractalAnalysisData(null);
                    setTerrainAnalysisData(null);
                    console.log('🔄 Start marker moved - routes cleared');
                  },
                }}
              >
                <Popup>
                  <div className="text-sm">
                    <strong>Start Point</strong><br />
                    {startLocationName && (
                      <>
                        📍 {startLocationName}<br />
                      </>
                    )}
                    Lat: {startMarker.lat.toFixed(4)}<br />
                    Lng: {startMarker.lng.toFixed(4)}
                  </div>
                </Popup>
              </Marker>
            )}

            {/* Destination Marker */}
            {destinationMarker && (
              <Marker
                position={destinationMarker}
                icon={destinationIcon}
                draggable={true}
                eventHandlers={{
                  dragend: (e) => {
                    setDestinationMarker(e.target.getLatLng());
                    setDestinationLocationName(''); // Reset name when dragged
                    // Clear routes and analysis when marker is moved
                    setRoutes([]);
                    setCandidateRoutes([]);
                    setRouteAnalysisData([]);
                    setShowRouteStats(false);
                    setFractalAnalysisData(null);
                    setTerrainAnalysisData(null);
                    console.log('🔄 Destination marker moved - routes cleared');
                  },
                }}
              >
                <Popup>
                  <div className="text-sm">
                    <strong>Destination</strong><br />
                    {destinationLocationName && (
                      <>
                        📍 {destinationLocationName}<br />
                      </>
                    )}
                    Lat: {destinationMarker.lat.toFixed(4)}<br />
                    Lng: {destinationMarker.lng.toFixed(4)}
                  </div>
                </Popup>
              </Marker>
            )}
          </MapContainer>
        </div>

        {/* Reroute Mode Indicator */}
        {rerouteMode && (
          <div className={`mt-4 p-4 ${theme === 'light' ? 'bg-amber-100 border border-amber-300' : 'bg-amber-500/10 border border-amber-500/30'} rounded-lg`}>
            <p className={`${theme === 'light' ? 'text-amber-800' : 'text-amber-300'} text-sm`}>
              <strong>🔄 Reroute Mode Active:</strong> The original route is shown in gray. Set your new start location (current position) and destination, then click "Analyze Mission" to calculate the reroute adjustments.
            </p>
          </div>
        )}

        {/* Map Instructions */}
        {inputMode === 'pin' && !rerouteMode && (
          <div className={`mt-4 p-4 ${theme === 'light' ? 'bg-blue-100 border border-blue-300' : 'bg-blue-500/10 border border-blue-500/30'} rounded-lg`}>
            <p className={`${theme === 'light' ? 'text-blue-800' : 'text-blue-300'} text-sm`}>
              <strong>Pin Mode Active:</strong> Click on the map to set start point (green), then click again to set destination (red).
            </p>
          </div>
        )}

        {/* Loading Indicator */}
        {isLoadingRoute && (
          <div className={`mt-4 p-4 ${theme === 'light' ? 'bg-yellow-100 border border-yellow-300' : 'bg-yellow-500/10 border border-yellow-500/30'} rounded-lg`}>
            <p className={`${theme === 'light' ? 'text-yellow-800' : 'text-yellow-300'} text-sm`}>
              <strong>Loading route...</strong> Fetching route data from routing services
            </p>
          </div>
        )}

        {/* Mock Data Warning for Routes */}
        {routes.length > 0 && routes[0].isMockData && (
          <div className={`mt-4 p-4 ${theme === 'light' ? 'bg-orange-100 border border-orange-300' : 'bg-orange-500/10 border border-orange-500/30'} rounded-lg`}>
            <p className={`${theme === 'light' ? 'text-orange-800' : 'text-orange-300'} text-sm`}>
              <strong>Notice:</strong> Using simulated route data. Routing services are currently unavailable.
            </p>
          </div>
        )}
      </div>



      {/* Individual Route Analysis Cards */}
      {routeAnalysisData.length > 0 && showTerrainAnalysis && (
        <>
          {routeAnalysisData.map((analysis, index) => (
            <RouteAnalysisCard
              key={index}
              analysisData={analysis}
            />
          ))}
        </>
      )}

      {/* ML Enhancement Button */}
      {routeAnalysisData.length > 0 && showTerrainAnalysis && !showEnhancedComparison && (
        <div className={`${themeClasses.card} border border-purple-500/50 rounded-xl p-6 mt-6 bg-gradient-to-br from-purple-500/10 to-blue-500/10`}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-xl font-bold text-purple-400 mb-2 flex items-center gap-2">
                <Sparkles size={24} />
                ML-Enhanced Terrain Analysis
              </h3>
              <p className={`text-sm ${themeClasses.textMuted} mb-2`}>
                Upgrade your terrain analysis from 30m to 10m resolution using machine learning. 
                This provides 3× more detailed terrain data for improved accuracy.
              </p>
              <div className="flex items-center gap-4 text-xs text-gray-400 mt-3">
                <div className="flex items-center gap-1">
                  <Clock size={14} />
                  <span>3-10 seconds</span>
                </div>
                {mlServiceHealthy !== null && (
                  <div className="flex items-center gap-1">
                    <div className={`w-2 h-2 rounded-full ${mlServiceHealthy ? 'bg-green-400' : 'bg-red-400'}`} />
                    <span>{mlServiceHealthy ? 'ML Service Ready' : 'ML Service Offline'}</span>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={handleEnhanceDEM}
              disabled={isEnhancing || !mlServiceHealthy}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-all transform hover:scale-105 disabled:hover:scale-100 font-semibold shadow-lg hover:shadow-xl flex items-center gap-2"
            >
              {isEnhancing ? (
                <>
                  <RefreshCw size={20} className="animate-spin" />
                  Enhancing...
                </>
              ) : (
                <>
                  <Sparkles size={20} />
                  Enhance to 10m Resolution
                </>
              )}
            </button>
          </div>
          {enhancementError && (
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-400">{enhancementError}</p>
            </div>
          )}
        </div>
      )}

      {/* Enhanced Comparison View */}
      {showEnhancedComparison && enhancedAnalysis && (
        <div className="mt-6">
          <EnhancedComparisonView
            analysis30m={enhancedAnalysis.analysis30m}
            analysis10m={enhancedAnalysis.analysis10m}
            enhancementMetadata={enhancedAnalysis.enhancement}
          />
          {/* Button to hide comparison */}
          <div className="mt-4 flex justify-center">
            <button
              onClick={() => setShowEnhancedComparison(false)}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all"
            >
              Hide Enhanced Analysis
            </button>
          </div>
        </div>
      )}

      {/* REAL Fractal Terrain Analysis */}
      {fractalAnalysisData && (
        <RealTerrainMetrics analysisData={fractalAnalysisData} />
      )}



      {/* Save Mission Reminder */}
      {routeAnalysisData.length > 0 && selectedVehicle && routeAnalysisData[selectedRouteIndex]?.componentStress && (
        <div className={`${themeClasses.card} border border-blue-500/50 rounded-xl p-6 mt-6 bg-blue-500/10`}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className={`text-xl font-bold text-blue-400 mb-2 flex items-center gap-2`}>
                <Save size={24} />
                Ready to Apply Mission to Vehicle
              </h3>
              <p className={`text-sm ${themeClasses.textMuted} mb-3`}>
                Your mission analysis is complete! To apply the predicted component wear to your vehicle, 
                you must first save this mission. This ensures proper tracking and allows for rerouting if needed.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">1</div>
                  <p className={`text-sm ${themeClasses.textPrimary}`}>
                    <strong>Save this mission</strong> using the "Save Mission" button below
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">2</div>
                  <p className={`text-sm ${themeClasses.textPrimary}`}>
                    <strong>Go to Saved Missions</strong> tab to review your saved mission
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">3</div>
                  <p className={`text-sm ${themeClasses.textPrimary}`}>
                    <strong>Apply to Vehicle</strong> from the Saved Missions page
                  </p>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <div className="bg-blue-500/20 border border-blue-500/30 px-3 py-1 rounded-lg">
                  <p className="text-blue-400 text-xs font-semibold">
                    Selected Vehicle: {vehicles.find(v => v.uniqueId === selectedVehicle)?.brand} {vehicles.find(v => v.uniqueId === selectedVehicle)?.model}
                  </p>
                </div>
                <div className="bg-green-500/20 border border-green-500/30 px-3 py-1 rounded-lg">
                  <p className="text-green-400 text-xs font-semibold">
                    ✓ Analysis Complete
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={handleSaveMission}
                disabled={!routes.length || !terrainAnalysisData || (loadedMissionId && !missionHasChanges)}
                className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-all transform hover:scale-105 disabled:hover:scale-100 font-semibold shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                <Save size={20} />
                {loadedMissionId && missionHasChanges ? 'Save Changes' : 'Save Mission'}
              </button>
              <p className="text-xs text-center text-gray-400">
                Then apply in Saved Missions
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Loading indicator for fractal analysis */}
      {isLoadingFractal && (
        <div className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-6 mt-6`}>
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            <p className={themeClasses.textPrimary}>
              Performing fractal box-counting analysis on SRTM data...
            </p>
          </div>
        </div>
      )}


      {/* Main Route Terrain Analysis Card (Legacy) */}
      {showTerrainAnalysis && terrainAnalysisData && !routeAnalysisData.length && (
        <TerrainAnalysisCard analysisData={terrainAnalysisData} />
      )}
      {/* Analyze Mission Processing Modal */}
      {showAnalysisModal && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-md flex items-center justify-center z-50">
          <style>{`
            @keyframes routeDash {
              0%   { stroke-dashoffset: 120; }
              100% { stroke-dashoffset: 0; }
            }
            @keyframes pinPulse {
              0%, 100% { opacity: 0.6; transform: scale(1); }
              50%       { opacity: 1;   transform: scale(1.15); }
            }
            @keyframes analysisShimmer {
              0%   { background-position: -200% 0; }
              100% { background-position:  200% 0; }
            }
          `}</style>

          <div className="bg-gray-900 border border-blue-500/40 rounded-2xl p-8 w-full max-w-lg mx-4 shadow-2xl">

            {/* ── Header ── */}
            {analysisSuccess ? (
              <div className="text-center mb-6">
                <div className="text-4xl mb-2">✅</div>
                <h2 className="text-white text-xl font-bold">Analysis Complete</h2>
                <p className="text-blue-300 text-sm mt-1">Mission ready in {formatElapsed(analysisElapsed)}</p>
              </div>
            ) : analysisError ? (
              <div className="text-center mb-6">
                <div className="text-4xl mb-2">❌</div>
                <h2 className="text-white text-xl font-bold">Analysis Failed</h2>
              </div>
            ) : (
              <div className="text-center mb-6">
                <h2 className="text-white text-xl font-bold">Analyzing Mission</h2>
                <p className="text-gray-400 text-sm mt-1">
                  SRTM Elevation • Terrain Physics • Fractal Analysis
                </p>
              </div>
            )}

            {/* ── Route animation ── */}
            {!analysisError && (
              <div className="flex items-center justify-center mb-6 bg-gray-800/50 rounded-xl p-5">
                <svg width="220" height="64" viewBox="0 0 220 64" fill="none">
                  {/* Start pin */}
                  <circle cx="20" cy="32" r="14" fill="#1d4ed8" opacity="0.9"
                    style={{ animation: analysisSuccess ? 'none' : 'pinPulse 1.8s ease-in-out infinite' }} />
                  <text x="20" y="37" textAnchor="middle" fill="white" fontSize="11" fontWeight="bold">S</text>

                  {/* Animated dashed route line */}
                  <line
                    x1="36" y1="32" x2="184" y2="32"
                    stroke={analysisSuccess ? '#22c55e' : '#6366f1'}
                    strokeWidth="2.5"
                    strokeDasharray="10 5"
                    strokeLinecap="round"
                    style={analysisSuccess ? {} : {
                      animation: 'routeDash 1.4s linear infinite',
                      strokeDashoffset: 0
                    }}
                  />

                  {/* Destination pin */}
                  <circle cx="200" cy="32" r="14" fill={analysisSuccess ? '#16a34a' : '#dc2626'} opacity="0.9"
                    style={{ animation: analysisSuccess ? 'none' : 'pinPulse 1.8s ease-in-out 0.9s infinite' }} />
                  <text x="200" y="37" textAnchor="middle" fill="white" fontSize="11" fontWeight="bold">D</text>
                </svg>
              </div>
            )}

            {/* ── Error message ── */}
            {analysisError && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                <p className="text-red-400 text-sm leading-relaxed">{analysisError}</p>
                <p className="text-gray-500 text-xs mt-2">Check: OpenTopography API key • Route area ≤ 25 deg²</p>
              </div>
            )}

            {/* ── Stage tracker ── */}
            {!analysisError && !analysisSuccess && (
              <div className="space-y-2.5 mb-6">
                {[
                  'Validating mission parameters',
                  'Fetching SRTM elevation data',
                  'Analyzing terrain physics',
                  'Running fractal complexity analysis',
                ].map((label, idx) => {
                  const isDone   = idx < analysisStage;
                  const isActive = idx === analysisStage;
                  return (
                    <div key={idx} className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold transition-colors duration-500 ${
                        isDone ? 'bg-green-500 text-white' : isActive ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-600'
                      }`}>
                        {isDone ? '✓' : isActive
                          ? <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                          : <div className="w-2 h-2 bg-gray-600 rounded-full" />
                        }
                      </div>
                      <span className={`text-sm transition-colors duration-500 ${
                        isDone ? 'text-green-400' : isActive ? 'text-white font-medium' : 'text-gray-600'
                      }`}>
                        {label}
                      </span>
                      {isActive && analysisRouteCount > 0 && idx === 1 && (
                        <span className="ml-auto text-xs text-gray-500 font-mono">{analysisRouteCount} route{analysisRouteCount > 1 ? 's' : ''}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Stats bar ── */}
            {!analysisError && (
              <div className="flex items-center justify-between text-xs font-mono text-gray-500 mb-4 bg-gray-800/60 rounded-lg px-3 py-2">
                <span>{analysisRouteCount} route{analysisRouteCount !== 1 ? 's' : ''}</span>
                <span>Elapsed: {formatElapsed(analysisElapsed)}</span>
                <span>SRTM 30m</span>
              </div>
            )}

            {/* ── Progress bar ── */}
            {!analysisError && (
              <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: analysisSuccess ? '100%' : `${Math.min((analysisStage / 4) * 100, 95)}%`,
                    background: analysisSuccess
                      ? '#22c55e'
                      : 'linear-gradient(90deg, #1d4ed8, #6366f1, #1d4ed8)',
                    backgroundSize: '200% 100%',
                    animation: analysisSuccess ? 'none' : 'analysisShimmer 2s linear infinite',
                  }}
                />
              </div>
            )}

            {/* ── Close button (error only) ── */}
            {analysisError && (
              <button
                onClick={() => { setShowAnalysisModal(false); setAnalysisError(null); }}
                className="mt-4 w-full px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm font-medium"
              >
                Close
              </button>
            )}
          </div>
        </div>
      )}

      {/* ML Enhancement Processing Modal */}
      {showEnhancementModal && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-md flex items-center justify-center z-50">
          <style>{`
            @keyframes enhanceScan {
              0%   { transform: translateX(-120%); opacity: 0; }
              15%  { opacity: 1; }
              85%  { opacity: 1; }
              100% { transform: translateX(120%); opacity: 0; }
            }
            @keyframes gridDot {
              0%, 100% { opacity: 0.25; }
              50%       { opacity: 1; }
            }
            @keyframes progressShimmer {
              0%   { background-position: -200% 0; }
              100% { background-position:  200% 0; }
            }
          `}</style>

          <div className="bg-gray-900 border border-purple-500/40 rounded-2xl p-8 w-full max-w-lg mx-4 shadow-2xl">

            {/* ── Header ── */}
            {enhancementSuccess ? (
              <div className="text-center mb-6">
                <div className="text-4xl mb-2">✅</div>
                <h2 className="text-white text-xl font-bold">Enhancement Complete</h2>
                <p className="text-purple-300 text-sm mt-1">
                  30m → 10m in {formatElapsed(enhancementElapsed)}
                </p>
              </div>
            ) : enhancementError ? (
              <div className="text-center mb-6">
                <div className="text-4xl mb-2">❌</div>
                <h2 className="text-white text-xl font-bold">Enhancement Failed</h2>
              </div>
            ) : (
              <div className="text-center mb-6">
                <h2 className="text-white text-xl font-bold">ML Enhancement Processing</h2>
                <p className="text-gray-400 text-sm mt-1">RCAN Neural Network • 30m → 10m Resolution</p>
              </div>
            )}

            {/* ── Grid animation ── */}
            {!enhancementError && (
              <div className="flex items-center justify-center gap-5 mb-6 bg-gray-800/50 rounded-xl p-4">
                {/* 30m coarse grid */}
                <div>
                  <div className="text-xs text-blue-400/70 text-center mb-1.5 font-mono tracking-widest">30m</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 3 }}>
                    {Array.from({ length: 25 }, (_, i) => (
                      <div key={i} className="bg-blue-400/35 rounded-sm" style={{ width: 11, height: 11 }} />
                    ))}
                  </div>
                </div>

                {/* Animated scan arrow */}
                <div className="relative flex items-center" style={{ width: 72, height: 8, overflow: 'hidden' }}>
                  <div className="absolute inset-y-0 left-0 right-4 top-1/2 h-px -translate-y-1/2 bg-gradient-to-r from-blue-500 via-purple-500 to-purple-500" />
                  {!enhancementSuccess && (
                    <div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/80 to-transparent"
                      style={{ animation: 'enhanceScan 1.8s linear infinite' }}
                    />
                  )}
                  <span className="absolute right-0 text-purple-400 font-bold" style={{ fontSize: 18, lineHeight: 1 }}>›</span>
                </div>

                {/* 10m fine grid */}
                <div>
                  <div className="text-xs text-purple-400 text-center mb-1.5 font-mono tracking-widest">10m</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: 1.5 }}>
                    {Array.from({ length: 100 }, (_, i) => {
                      const delay = ((i % 10) * 0.065 + Math.floor(i / 10) * 0.045) % 1.6;
                      return (
                        <div
                          key={i}
                          className="rounded-sm"
                          style={{
                            width: 5,
                            height: 5,
                            backgroundColor: enhancementSuccess ? '#a78bfa' : '#a78bfa',
                            opacity: enhancementSuccess ? 1 : undefined,
                            animation: enhancementSuccess ? 'none' : `gridDot 1.6s ease-in-out ${delay}s infinite`,
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* ── Error message ── */}
            {enhancementError && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                <p className="text-red-400 text-sm leading-relaxed">{enhancementError}</p>
              </div>
            )}

            {/* ── Stage tracker ── */}
            {!enhancementError && !enhancementSuccess && (
              <div className="space-y-2.5 mb-6">
                {['Preparing elevation grid', 'Sending to ML service', 'Running RCAN neural network', 'Re-analyzing terrain at 10m'].map((label, idx) => {
                  const stage = getEnhancementStage(enhancementElapsed, enhancementGridInfo);
                  const isDone   = idx < stage;
                  const isActive = idx === stage;
                  const est = getEstimatedDuration(enhancementGridInfo);
                  const remaining = Math.max(0, est - enhancementElapsed);
                  return (
                    <div key={idx} className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold transition-colors duration-500 ${
                        isDone ? 'bg-green-500 text-white' : isActive ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-600'
                      }`}>
                        {isDone ? '✓' : isActive ? (
                          <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                        ) : (
                          <div className="w-2 h-2 bg-gray-600 rounded-full" />
                        )}
                      </div>
                      <span className={`text-sm transition-colors duration-500 ${
                        isDone ? 'text-green-400' : isActive ? 'text-white font-medium' : 'text-gray-600'
                      }`}>
                        {label}
                      </span>
                      {isActive && idx === 2 && remaining > 0 && (
                        <span className="ml-auto text-xs text-gray-500 font-mono">~{remaining}s left</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Stats bar ── */}
            {!enhancementError && (
              <div className="flex items-center justify-between text-xs font-mono text-gray-500 mb-4 bg-gray-800/60 rounded-lg px-3 py-2">
                <span>{enhancementGridInfo ? `Grid: ${enhancementGridInfo.rows}×${enhancementGridInfo.cols}` : 'Grid: detecting…'}</span>
                <span>Elapsed: {formatElapsed(enhancementElapsed)}</span>
                <span>Device: CPU</span>
              </div>
            )}

            {/* ── Progress bar ── */}
            {!enhancementError && (
              <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-1000"
                  style={{
                    width: enhancementSuccess
                      ? '100%'
                      : `${Math.min((enhancementElapsed / getEstimatedDuration(enhancementGridInfo)) * 94, 94)}%`,
                    background: enhancementSuccess
                      ? '#22c55e'
                      : 'linear-gradient(90deg, #7c3aed, #3b82f6, #7c3aed)',
                    backgroundSize: '200% 100%',
                    animation: enhancementSuccess ? 'none' : 'progressShimmer 2s linear infinite',
                  }}
                />
              </div>
            )}

            {/* ── Close button (error only) ── */}
            {enhancementError && (
              <button
                onClick={() => { setShowEnhancementModal(false); setEnhancementError(null); }}
                className="mt-2 w-full px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm font-medium"
              >
                Close
              </button>
            )}
          </div>
        </div>
      )}

      {/* Save Options Modal */}
      {showSaveOptionsModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} border ${themeClasses.border} rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl`}>
            <div className="flex items-center gap-3 mb-4">
              <Save className="text-blue-400" size={24} />
              <h3 className={`${themeClasses.textPrimary} font-semibold text-lg`}>Mission Modified</h3>
            </div>
            
            <p className={`${themeClasses.textSecondary} mb-6`}>
              You've made changes to <strong>"{missionName}"</strong>.
              <br /><br />
              {rerouteMode ? 
                'Update the mission with the new route. The mission will return to "planned" status and must be re-applied to the vehicle.' :
                'How would you like to save these changes?'
              }
            </p>
            
            <div className="space-y-3">
              <button
                onClick={() => performSave(true)}
                className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
              >
                <Save size={18} />
                Update Existing Mission
              </button>
              
              {!rerouteMode && (
                <button
                  onClick={() => performSave(false)}
                  className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  <Plus size={18} />
                  Save as New Mission
                </button>
              )}
              
              <button
                onClick={() => setShowSaveOptionsModal(false)}
                className={`w-full px-4 py-3 ${themeClasses.buttonSecondary} rounded-lg transition-colors font-medium`}
              >
                Cancel
              </button>
            </div>
            
            {!rerouteMode && (
              <div className={`mt-4 p-3 ${theme === 'dark' ? 'bg-blue-500/10' : 'bg-blue-100'} border ${theme === 'dark' ? 'border-blue-500/30' : 'border-blue-300'} rounded-lg`}>
                <p className={`text-xs ${theme === 'dark' ? 'text-blue-300' : 'text-blue-800'}`}>
                  <strong>Update:</strong> Overwrites the original mission<br />
                  <strong>Save as New:</strong> Creates a copy with your changes
                </p>
              </div>
            )}
            
            {rerouteMode && (
              <div className={`mt-4 p-3 ${theme === 'dark' ? 'bg-amber-500/10' : 'bg-amber-100'} border ${theme === 'dark' ? 'border-amber-500/30' : 'border-amber-300'} rounded-lg`}>
                <p className={`text-xs ${theme === 'dark' ? 'text-amber-300' : 'text-amber-800'}`}>
                  <strong>Reroute Mode:</strong> Mission will be updated with the new route and reset to "planned" status. You'll need to re-apply it to the vehicle.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TerrainIntelligence;
